import type { FormEvent, ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Anchor,
  Calendar,
  Calculator,
  CheckCircle2,
  ChevronDown,
  Eye,
  FilePlus2,
  FileText,
  History,
  Loader2,
  Mail,
  Map,
  MapPin,
  MessageSquare,
  Navigation,
  Package,
  Pencil,
  Plus,
  ReceiptText,
  RefreshCw,
  Route,
  Save,
  Ship,
  Sparkles,
  Trash2,
  Truck,
  Upload,
  Warehouse,
  XCircle,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { ApiClientError, apiClient } from '@/lib/api/client'
import { formatCurrency } from '@/lib/utils'
import { CommunicationAttachmentPicker } from '@/src/components/CommunicationAttachmentPicker'
import { ErrorState } from '@/src/components/ErrorState'
import { HelmCcSelector } from '@/src/components/HelmCcSelector'
import { HelmOwnerCombobox, HelmOwnerName } from '@/src/components/HelmOwnerCombobox'
import { consignmentLabel } from '@/src/components/QuickEdit'
import { DueDateEditor } from '@/src/components/DueDateEditor'
import { customerGreeting, shipmentReference } from '@/lib/customer-messages'
import { RecordActivity } from '@/src/components/RecordActivity'
import { LoadingState } from '@/src/components/LoadingState'
import { HintedLabel } from '@/src/components/FieldHint'
import { FIELD_HELP } from '@/src/lib/onboarding/field-help'
import { EnumSelect } from '@/components/ui/enum-select'
import { AIR_SERVICES, shipmentLabel } from '@/src/lib/air-service'
import { PublicTrackingShareManager } from '@/src/components/tracking/PublicTrackingShareManager'
import { GatheringInChinaCard } from '@/src/components/tracking/GatheringInChinaCard'
import { GATHERING_STATE, gatheringSummary } from '@/src/lib/parcels'
import { ServiceTypeMultiSelect } from '@/src/components/ServiceTypeMultiSelect'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import type {
  ClassifyResponse,
  Consignment,
  ConsignmentDocument,
  ConsignmentDocumentDefinition,
  ConsignmentDocumentType,
  ConsignmentDetail,
  ConsignmentGathering,
  ConsignmentItem,
  ConsignmentWorkflowPhase,
  ConsignmentSeaContainer,
  ConsignmentSeaShipment,
  GoxBookingCargoRow,
  GoxBookingNotePayload,
  HelmCommunication,
  HelmAirService,
  HelmCommunicationChannel,
  HelmOutboundCommunicationAttachment,
  HelmServiceType,
  ApplyBillOfLadingProposalRequest,
  BillOfLadingReviewProposal,
  MaritimeCatalogs,
  MaritimeContainerReconciliation,
  MaritimeContainerStatus,
  MaritimeEquipmentCode,
  MaritimeReferenceKind,
  MaritimeShipmentStatus,
  SeaTrackingContainer,
  SeaTrackingShipment,
  SeaTrackingType,
  SeaVesselCurrentPosition,
  RequiredDocumentChecklistItem,
  UpdateConsignmentRequest,
  UpdateConsignmentItemRequest,
  WorkflowOperationalState,
  WorkflowTaskStatus,
} from '@/lib/api/types'
import {
  getConsignmentSeaTrackingTarget,
  isContainerReference,
  referenceKindForTrackingType,
  shouldLoadSeaTracking,
  trackingTypeForReferenceKind,
} from '@/src/lib/tracking'
import {
  buildRouteMapPoints,
  buildRoutePathSegments,
  buildRouteProgressSteps,
  buildSeaRouteSteps,
  describeContainerMovement,
  flattenRouteSegments,
  formatLabel,
  formatLocationName,
  getSeaCurrentPosition,
  getShipmentContainerCount,
  getVesselCurrentPosition,
  readFirstDate,
  readFirstString,
  readRecord,
  readRecordArray,
  readString,
  validPositionTimestamp,
} from '@/src/lib/sea-route'
import type { SeaCurrentPosition, SeaMovement, SeaRouteStep } from '@/src/lib/sea-route'
import { ROUTE_MARKER_SHADOW, routeColors } from '@/src/lib/route-colors'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { FactCard } from '@/components/ui/stat'
import {
  Translatable,
  TranslatableMessage,
  TranslateToggle,
  Translated,
} from '@/src/components/translation/Translatable'

const statusOptions: Array<NonNullable<UpdateConsignmentRequest['status']>> = [
  'DRAFT',
  'AWAITING_INVOICE_FROM_FORWARDER',
  'AWAITING_PAYMENT',
  'PAYMENT_CONFIRMED',
  'IN_TRANSIT_DOMESTIC',
  'IN_TRANSIT_INTERNATIONAL',
  'ARRIVED',
  'CLEARING',
  'READY_FOR_PICKUP',
  'DELIVERED',
  'CLOSED',
]

const consignmentPhaseTransitions: Record<ConsignmentWorkflowPhase, ConsignmentWorkflowPhase[]> = {
  HANDOFF: ['PREPARATION'],
  PREPARATION: ['HANDOFF', 'IN_TRANSIT'],
  IN_TRANSIT: ['PREPARATION', 'CLEARANCE', 'DELIVERY'],
  CLEARANCE: ['IN_TRANSIT', 'DELIVERY'],
  DELIVERY: ['CLEARANCE', 'COMPLETED'],
  COMPLETED: ['DELIVERY'],
}

const operationalStateTransitions: Record<WorkflowOperationalState, WorkflowOperationalState[]> = {
  ACTIVE: ['BLOCKED', 'COMPLETED', 'CANCELLED'],
  BLOCKED: ['ACTIVE', 'CANCELLED'],
  COMPLETED: ['ACTIVE'],
  CANCELLED: ['ACTIVE'],
}

const communicationChannelOptions: HelmCommunicationChannel[] = [
  'EMAIL',
  'WHATSAPP',
  'BOTH',
  'NONE',
]
const seaTrackingTypeOptions: SeaTrackingType[] = [
  'container',
  'booking',
  'bill_of_lading',
  'without_tracking',
]
const maritimeShipmentStatusOptions: MaritimeShipmentStatus[] = [
  'DRAFT',
  'BOOKED',
  'CONFIRMED',
  'IN_TRANSIT',
  'ARRIVED',
  'COMPLETED',
  'CANCELLED',
]
const maritimeContainerStatusOptions: MaritimeContainerStatus[] = [
  'PLANNED',
  'BOOKED',
  'GATE_IN',
  'LOADED',
  'IN_TRANSIT',
  'DISCHARGED',
  'GATE_OUT',
  'DELIVERED',
  'EMPTY_RETURNED',
]
const maritimeReferenceKindOptions: MaritimeReferenceKind[] = [
  'BOOKING',
  'MASTER_BILL_OF_LADING',
  'HOUSE_BILL_OF_LADING',
  'CONTAINER',
]
const maritimeEquipmentFallback: MaritimeEquipmentCode[] = [
  '20GP',
  '40GP',
  '40HC',
  '20RF',
  '40RF',
  '20OT',
  '40OT',
  '20FR',
  '40FR',
]

/**
 * The shipment's own tracking: its B/L or container, or its air waybill. Not a
 * courier's package number, which follows one parcel inside it.
 */
function shipmentTracking(
  consignment: Consignment & Pick<Partial<ConsignmentDetail>, 'sea_shipments'>,
) {
  const firstSeaShipment = consignment.sea_shipments?.find(
    (shipment) =>
      shipment.reference?.trim() ||
      shipment.containers.some((container) => container.container_number?.trim()),
  )
  const seaReference =
    firstSeaShipment?.reference ||
    firstSeaShipment?.containers.find((container) => container.container_number?.trim())
      ?.container_number
  if (seaReference) {
    const shipmentCount = consignment.sea_shipments?.length ?? 0
    const containerCount =
      consignment.sea_shipments?.reduce((sum, shipment) => sum + shipment.containers.length, 0) ?? 0
    if (containerCount > 1) return `${seaReference} +${containerCount - 1} containers`
    if (shipmentCount > 1) return `${seaReference} +${shipmentCount - 1} shipments`
    return seaReference
  }
  return consignment.international_tracking_number || null
}

/** The shipment's own tracking for display; a package's courier number is never it. */
function displayTracking(
  consignment: Consignment & Pick<Partial<ConsignmentDetail>, 'sea_shipments'>,
) {
  return shipmentTracking(consignment) || consignment.tracking_number || 'Not assigned'
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'Not set'
  return new Date(value).toLocaleDateString()
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return 'Not set'
  return new Date(value).toLocaleString()
}

function toDateTimeLocal(value: string | null | undefined) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const timezoneOffset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16)
}

function normalizeWhatsAppPhone(phone: string | null | undefined) {
  return phone?.replace(/\D/g, '') || ''
}

function communicationCcRecipients(communication: HelmCommunication) {
  const cc = communication.metadata?.cc
  return Array.isArray(cc)
    ? cc.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : []
}

type CarrierProfile = {
  code: string
  name: string
  logoUrl: string | null
}

const carrierProfiles: Record<string, CarrierProfile> = {
  PIL: {
    code: 'PIL',
    name: 'Pacific International Lines',
    logoUrl: null,
  },
  MAERSK: {
    code: 'MAEU',
    name: 'Maersk',
    logoUrl: null,
  },
  MSC: {
    code: 'MSC',
    name: 'Mediterranean Shipping Company',
    logoUrl: null,
  },
  CMA: {
    code: 'CMA',
    name: 'CMA CGM',
    logoUrl: null,
  },
  COSCO: {
    code: 'COSCO',
    name: 'COSCO Shipping',
    logoUrl: null,
  },
  ONE: {
    code: 'ONE',
    name: 'Ocean Network Express',
    logoUrl: null,
  },
  HAPAG: {
    code: 'HLC',
    name: 'Hapag-Lloyd',
    logoUrl: null,
  },
  EVERGREEN: {
    code: 'EGLV',
    name: 'Evergreen Marine',
    logoUrl: '/carriers/evergreen-roundel.svg',
  },
  DEFAULT: {
    code: 'SEA',
    name: 'Ocean carrier pending',
    logoUrl: null,
  },
}

function getCarrierProfile(
  shipment: SeaTrackingShipment | null | undefined,
  container: SeaTrackingContainer | undefined,
): CarrierProfile {
  const carrierRecord = readRecord(shipment?.raw?.carrier) ?? readRecord(container?.raw?.carrier)
  const carrierValue =
    shipment?.carrier_scac ||
    readString(shipment?.raw, 'carrier_scac') ||
    readString(shipment?.raw, 'carrier_name') ||
    readString(container?.raw, 'carrier_scac') ||
    readString(container?.raw, 'carrier_name') ||
    readString(carrierRecord, 'scac') ||
    readString(carrierRecord, 'name') ||
    readString(container?.last_movement, 'carrier')
  const logoUrl = getCarrierLogoUrl(shipment, container, carrierRecord)
  const withProviderLogo = (profile: CarrierProfile): CarrierProfile => ({
    ...profile,
    logoUrl: logoUrl ?? profile.logoUrl,
  })
  const normalized = (carrierValue || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
  if (normalized.includes('MAERSK') || normalized.includes('MAEU'))
    return withProviderLogo(carrierProfiles.MAERSK)
  if (normalized.includes('PIL') || normalized.includes('PCIU'))
    return withProviderLogo(carrierProfiles.PIL)
  if (normalized.includes('MSCU') || normalized.includes('MSC'))
    return withProviderLogo(carrierProfiles.MSC)
  if (normalized.includes('CMA') || normalized.includes('CGM'))
    return withProviderLogo(carrierProfiles.CMA)
  if (normalized.includes('COSCO') || normalized.includes('COSU'))
    return withProviderLogo(carrierProfiles.COSCO)
  if (normalized.includes('ONE') || normalized.includes('ONEY'))
    return withProviderLogo(carrierProfiles.ONE)
  if (normalized.includes('HAPAG') || normalized.includes('HLCU'))
    return withProviderLogo(carrierProfiles.HAPAG)
  if (
    normalized.includes('EVERGREEN') ||
    normalized.includes('EGLV') ||
    normalized.includes('EMCU') ||
    normalized === 'EMC'
  )
    return withProviderLogo(carrierProfiles.EVERGREEN)
  if (carrierValue) {
    return {
      ...carrierProfiles.DEFAULT,
      code: carrierValue.slice(0, 5).toUpperCase(),
      name: carrierValue,
      logoUrl,
    }
  }
  return { ...carrierProfiles.DEFAULT, logoUrl }
}

function getCarrierLogoUrl(
  shipment: SeaTrackingShipment | null | undefined,
  container: SeaTrackingContainer | undefined,
  carrierRecord: Record<string, unknown> | null,
) {
  const candidate =
    readString(carrierRecord, 'logo_url') ||
    readString(carrierRecord, 'logo') ||
    readString(carrierRecord, 'icon_url') ||
    readString(shipment?.raw, 'carrier_logo_url') ||
    readString(shipment?.raw, 'carrier_logo') ||
    readString(shipment?.raw, 'logo_url') ||
    readString(container?.raw, 'carrier_logo_url') ||
    readString(container?.raw, 'carrier_logo') ||
    readString(container?.raw, 'logo_url')
  return candidate && /^https:\/\//i.test(candidate) ? candidate : null
}

function CarrierCodeChip({ profile }: { profile: CarrierProfile }) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground"
      data-testid="carrier-code-chip"
      title={`${profile.name} carrier code`}
    >
      <Ship className="h-3.5 w-3.5" />
      <span className="text-[10px] uppercase tracking-wide">SCAC</span>
      {profile.code}
    </span>
  )
}

function CarrierLogoOrCode({ profile }: { profile: CarrierProfile }) {
  if (profile.logoUrl) {
    return (
      <span
        className="inline-flex h-10 w-16 shrink-0 items-center justify-center rounded-md border bg-background p-1"
        title={profile.name}
      >
        <img
          src={profile.logoUrl}
          alt={`${profile.name} logo`}
          className="max-h-full max-w-full object-contain"
          data-testid="carrier-logo"
        />
      </span>
    )
  }
  return <CarrierCodeChip profile={profile} />
}

function CarrierBadge({ profile }: { profile: CarrierProfile }) {
  return (
    <span className="inline-flex max-w-full items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-xs font-medium">
      <CarrierLogoOrCode profile={profile} />
      <span className="truncate text-foreground">{profile.name}</span>
    </span>
  )
}

function formatPositionAge(value: string | null | undefined, now: number) {
  const timestamp = validPositionTimestamp(value)
  if (timestamp === null || !Number.isFinite(now)) return null

  const ageMs = Math.max(0, now - timestamp)
  const minutes = Math.floor(ageMs / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hr ago`

  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

function hasCurrentVesselPosition(position: SeaCurrentPosition | null) {
  return (
    (position?.source === 'AISSTREAM' || position?.source === 'DATA_DOCKED') &&
    (position.precision === 'LIVE' || position.precision === 'RECENT')
  )
}

function isVesselReportedPosition(position: SeaCurrentPosition | null) {
  return position?.source === 'AISSTREAM' || position?.source === 'DATA_DOCKED'
}

function chooseSeaCurrentPosition(
  aisPosition: SeaCurrentPosition | null,
  visiwisePosition: SeaCurrentPosition | null,
): SeaCurrentPosition | null {
  if (!aisPosition) return visiwisePosition
  if (!visiwisePosition) return aisPosition

  const aisTimestamp = validPositionTimestamp(aisPosition.date)
  const visiwiseTimestamp = validPositionTimestamp(visiwisePosition.date)

  if (aisTimestamp !== null && visiwiseTimestamp !== null) {
    return visiwiseTimestamp > aisTimestamp ? visiwisePosition : aisPosition
  }
  if (visiwiseTimestamp !== null && aisPosition.precision === 'STALE') {
    return visiwisePosition
  }
  if (aisTimestamp !== null) return aisPosition
  if (visiwiseTimestamp !== null) return visiwisePosition

  return aisPosition.precision === 'STALE' ? visiwisePosition : aisPosition
}

function getPositionLabel(position: SeaCurrentPosition | null) {
  if (!position) return 'Unavailable'
  if (position.source === 'DATA_DOCKED') {
    if (position.precision === 'STALE') return 'Last Data Docked report'
    return 'Data Docked position'
  }
  if (position.precision === 'LIVE') return 'Live AIS'
  if (position.precision === 'RECENT') return 'Recent AIS'
  if (position.precision === 'STALE') return 'Last AIS report'
  if (position.precision === 'PROVIDER_REPORTED') return 'Provider reported'
  return 'Last milestone'
}

function describeCurrentJourney(position: SeaCurrentPosition | null, routeSteps: SeaRouteStep[]) {
  if (!position) return 'Current location unavailable'
  if (position.source === 'AISSTREAM' || position.source === 'DATA_DOCKED') return position.name

  const normalizedEvent = position.event?.toLowerCase() || ''
  const destination = routeSteps.find((step) => step.label === 'POD')
  const destinationTime = destination?.date ? new Date(destination.date).getTime() : Number.NaN
  const departedOnVessel =
    normalizedEvent.includes('load') ||
    normalizedEvent.includes('vessel') ||
    normalizedEvent.includes('departure')
  if (
    departedOnVessel &&
    destination &&
    (!Number.isFinite(destinationTime) || destinationTime > Date.now())
  ) {
    return `At sea toward ${destination.name}`
  }
  return position.name
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (character) => {
    const replacements: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }
    return replacements[character] ?? character
  })
}

function collectSeaMovements(
  containers: SeaTrackingContainer[],
  routeSteps: SeaRouteStep[],
): SeaMovement[] {
  const records = containers.flatMap((container) => extractMovementRecords(container.raw))
  const normalized = records
    .map((record, index) => normalizeSeaMovementRecord(record, index))
    .filter((movement): movement is SeaMovement => Boolean(movement))
  const carriageMovements = containers.flatMap((container, containerIndex) =>
    buildContainerCarriageMovements(container, containerIndex),
  )
  const fallback =
    carriageMovements.length > 0
      ? []
      : routeSteps.map((step, index) => ({
          key: `route-${step.label}-${index}`,
          date: step.date,
          location: step.name,
          event: step.label,
          mode: index === 0 || index === routeSteps.length - 1 ? 'Truck' : 'Vessel',
          vessel: null,
        }))
  const lastMovement = containers[0]?.last_movement
  const lastMovementRecord = lastMovement ? normalizeSeaMovementRecord(lastMovement, 10_000) : null
  return dedupeSeaMovements([
    ...normalized,
    ...carriageMovements,
    ...fallback,
    ...(lastMovementRecord ? [lastMovementRecord] : []),
  ]).sort((a, b) => {
    if (!a.date && !b.date) return 0
    if (!a.date) return 1
    if (!b.date) return -1
    return new Date(a.date).getTime() - new Date(b.date).getTime()
  })
}

function extractMovementRecords(
  raw: Record<string, unknown> | undefined,
  depth = 0,
): Array<Record<string, unknown>> {
  if (!raw || depth > 1) return []
  const directKeys = [
    'movements',
    'events',
    'tracking_events',
    'container_events',
    'milestones',
    'activities',
    'history',
  ]
  const records = directKeys.flatMap((key) => readRecordArray(raw[key]))
  const nestedKeys = ['tracking', 'data', 'route', 'shipment']
  return [
    ...records,
    ...nestedKeys.flatMap((key) =>
      extractMovementRecords(readRecord(raw[key]) ?? undefined, depth + 1),
    ),
  ]
}

function buildContainerCarriageMovements(
  container: SeaTrackingContainer,
  containerIndex: number,
): SeaMovement[] {
  const movements: Array<SeaMovement | null> = [
    carriageMovement({
      containerIndex,
      record: container.pre,
      event: 'Container To Shipper',
      dateKeys: [
        'container_to_shipper_time',
        'gate_out',
        'departure_actual_time',
        'departure_time',
        'actual_at',
        'date',
      ],
      mode: 'Truck',
      sequence: 0,
    }),
    carriageMovement({
      containerIndex,
      record: container.pol,
      event: 'Load On Vessel',
      dateKeys: [
        'departure_actual_time',
        'departure_time',
        'departure_estimated_time',
        'departed_at',
        'departure_at',
        'atd',
        'actual_at',
      ],
      mode: 'Vessel',
      vesselNameKeys: ['departing_vessel_name', 'departure_vessel_name', 'vessel_name', 'vessel'],
      voyageKeys: [
        'departing_vessel_voyage_no',
        'departing_vessel_voyage_number',
        'voyage_no',
        'voyage_number',
        'voyage',
      ],
      sequence: 1,
    }),
    ...readRecordArray(container.transshipments).flatMap((transshipment, index) => [
      carriageMovement({
        containerIndex,
        record: transshipment,
        event: 'Discharge From Vessel',
        dateKeys: [
          'arrival_actual_time',
          'arrival_actual_time_carrier',
          'arrival_time',
          'arrival_estimated_time',
          'arrival_estimated_time_carrier',
          'arrived_at',
          'arrival_at',
          'ata',
        ],
        mode: 'Vessel',
        vesselNameKeys: [
          'arriving_vessel_name',
          'arrival_vessel_name',
          'inbound_vessel_name',
          'vessel_name',
          'vessel',
        ],
        voyageKeys: [
          'arriving_vessel_voyage_no',
          'arriving_vessel_voyage_number',
          'inbound_voyage_no',
          'voyage_no',
          'voyage_number',
          'voyage',
        ],
        sequence: 10 + index * 2,
      }),
      carriageMovement({
        containerIndex,
        record: transshipment,
        event: 'Load On Vessel',
        dateKeys: [
          'departure_actual_time',
          'departure_time',
          'departure_estimated_time',
          'departed_at',
          'departure_at',
          'atd',
        ],
        mode: 'Vessel',
        vesselNameKeys: [
          'departing_vessel_name',
          'departure_vessel_name',
          'outbound_vessel_name',
          'vessel_name',
          'vessel',
        ],
        voyageKeys: [
          'departing_vessel_voyage_no',
          'departing_vessel_voyage_number',
          'outbound_voyage_no',
          'voyage_no',
          'voyage_number',
          'voyage',
        ],
        sequence: 11 + index * 2,
      }),
    ]),
    carriageMovement({
      containerIndex,
      record: container.pod,
      event: 'Arrived At POD',
      dateKeys: [
        'arrival_actual_time',
        'arrival_actual_time_carrier',
        'arrival_time',
        'arrival_estimated_time',
        'arrival_estimated_time_carrier',
        'arrived_at',
        'arrival_at',
        'ata',
      ],
      mode: 'Vessel',
      vesselNameKeys: ['arriving_vessel_name', 'arrival_vessel_name', 'vessel_name', 'vessel'],
      voyageKeys: [
        'arriving_vessel_voyage_no',
        'arriving_vessel_voyage_number',
        'voyage_no',
        'voyage_number',
        'voyage',
      ],
      sequence: 100,
    }),
    carriageMovement({
      containerIndex,
      record: container.pod,
      event: 'Discharge From Vessel',
      dateKeys: ['discharge_actual_time', 'discharge_time', 'discharged_at'],
      mode: 'Vessel',
      vesselNameKeys: ['arriving_vessel_name', 'arrival_vessel_name', 'vessel_name', 'vessel'],
      voyageKeys: [
        'arriving_vessel_voyage_no',
        'arriving_vessel_voyage_number',
        'voyage_no',
        'voyage_number',
        'voyage',
      ],
      sequence: 101,
    }),
    carriageMovement({
      containerIndex,
      record: container.pod,
      event: 'Import Gate Out',
      dateKeys: ['gate_out', 'gate_out_time', 'actual_gate_out_time', 'empty_gate_out_time'],
      mode: 'Truck',
      sequence: 102,
    }),
    carriageMovement({
      containerIndex,
      record: container.pde,
      event: 'Place Of Delivery Arrival',
      dateKeys: [
        'arrival_actual_time',
        'arrival_time',
        'arrival_estimated_time',
        'arrived_at',
        'arrival_at',
        'ata',
      ],
      mode: 'Truck',
      sequence: 200,
    }),
  ]

  return movements.filter((movement): movement is SeaMovement => Boolean(movement))
}

function carriageMovement({
  containerIndex,
  record,
  event,
  dateKeys,
  mode,
  vesselNameKeys = [],
  voyageKeys = [],
  sequence,
}: {
  containerIndex: number
  record: Record<string, unknown> | null | undefined
  event: string
  dateKeys: string[]
  mode: string
  vesselNameKeys?: string[]
  voyageKeys?: string[]
  sequence: number
}): SeaMovement | null {
  const source = readRecord(record)
  if (!source) return null
  const location = formatLocationName(source)
  const date = readFirstDate(source, dateKeys)
  if (!location && !date) return null
  const vessel =
    [readFirstString(source, vesselNameKeys), readFirstString(source, voyageKeys)]
      .filter(Boolean)
      .join(' ') || null

  return {
    key: `carriage-${containerIndex}-${sequence}-${date ?? 'pending'}-${location ?? 'unknown'}-${event}`,
    date,
    location: location || 'Location pending',
    event,
    mode,
    vessel,
  }
}

function normalizeSeaMovementRecord(
  record: Record<string, unknown>,
  index: number,
): SeaMovement | null {
  const nestedLocation =
    readRecord(record.location) ?? readRecord(record.port) ?? readRecord(record.terminal)
  const event =
    readString(record, 'event') ||
    readString(record, 'event_name') ||
    readString(record, 'event_type') ||
    readString(record, 'status') ||
    readString(record, 'description') ||
    readString(record, 'activity') ||
    readString(record, 'name')
  const location =
    readString(record, 'location') ||
    readString(record, 'location_name') ||
    readString(record, 'port_name') ||
    readString(record, 'terminal_name') ||
    readString(nestedLocation, 'name') ||
    readString(nestedLocation, 'port_name')
  if (!event && !location) return null
  const date =
    readString(record, 'date') ||
    readString(record, 'datetime') ||
    readString(record, 'event_at') ||
    readString(record, 'actual_at') ||
    readString(record, 'estimated_at') ||
    readString(record, 'timestamp') ||
    readString(record, 'time') ||
    readString(record, 'ata') ||
    readString(record, 'atd') ||
    readString(record, 'created_at')
  const vessel =
    [
      readString(record, 'vessel_name') || readString(record, 'vessel'),
      readString(record, 'voyage_no') ||
        readString(record, 'voyage_number') ||
        readString(record, 'voyage'),
    ]
      .filter(Boolean)
      .join(' ') || null
  const mode =
    readString(record, 'transport_mode') ||
    readString(record, 'transport_type') ||
    readString(record, 'mode') ||
    inferTransportMode(event, vessel)
  return {
    key: `${date ?? index}-${location ?? 'unknown'}-${event ?? 'movement'}-${index}`,
    date,
    location: location || 'Location pending',
    event: event ? formatLabel(event) : 'Movement',
    mode,
    vessel,
  }
}

function inferTransportMode(event: string | null | undefined, vessel: string | null) {
  const normalized = event?.toLowerCase() ?? ''
  if (
    vessel ||
    normalized.includes('vessel') ||
    normalized.includes('load') ||
    normalized.includes('discharge')
  )
    return 'Vessel'
  if (normalized.includes('rail')) return 'Rail'
  return 'Truck'
}

function dedupeSeaMovements(movements: SeaMovement[]) {
  const seen = new Set<string>()
  return movements.filter((movement) => {
    const key = `${movement.date ?? ''}:${movement.location}:${movement.event}`.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function modeIcon(mode: string) {
  const normalized = mode.toLowerCase()
  if (normalized.includes('vessel') || normalized.includes('ship')) return Ship
  return Truck
}

function SeaRouteMap({
  routeSteps,
  containers,
  reference,
  currentPosition,
}: {
  routeSteps: SeaRouteStep[]
  containers: SeaTrackingContainer[]
  reference: string
  currentPosition: SeaCurrentPosition | null
}) {
  const mapElementRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const points = useMemo(
    () => buildRouteMapPoints(routeSteps, containers),
    [routeSteps, containers],
  )

  useEffect(() => {
    if (!mapElementRef.current || points.length === 0) return

    if (mapRef.current) {
      mapRef.current.remove()
      mapRef.current = null
    }

    const map = L.map(mapElementRef.current, {
      attributionControl: true,
      scrollWheelZoom: false,
      zoomControl: true,
    })
    mapRef.current = map

    // OpenStreetMap's own tiles, which need no key. CARTO's basemap CDN now
    // requires one and stamps "API KEY REQUIRED" across every unauthenticated
    // tile, which is what the route map was rendering over.
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map)

    const routeSegments = buildRoutePathSegments(points)
    const routeLine = flattenRouteSegments(routeSegments).map((point) =>
      L.latLng(point.latitude, point.longitude),
    )
    const currentIndex = points.findIndex((point) => point.current)
    const actualLine =
      currentIndex > 0
        ? flattenRouteSegments(
            routeSegments.filter((segment) => segment.toIndex <= currentIndex),
          ).map((point) => L.latLng(point.latitude, point.longitude))
        : []
    const plannedLine =
      currentIndex >= 0
        ? flattenRouteSegments(
            routeSegments.filter((segment) => segment.fromIndex >= currentIndex),
          ).map((point) => L.latLng(point.latitude, point.longitude))
        : routeLine

    const colors = routeColors()
    if (plannedLine.length > 1) {
      L.polyline(plannedLine, {
        className: 'sea-route-planned-line',
        color: colors.planned,
        dashArray: '8 8',
        opacity: 0.7,
        weight: 3,
      }).addTo(map)
    }

    if (actualLine.length > 1) {
      L.polyline(actualLine, {
        className: 'sea-route-actual-line',
        color: colors.actual,
        opacity: 0.95,
        weight: 4,
      }).addTo(map)
    }

    for (const point of points) {
      const color = point.current
        ? colors.current
        : point.completed
          ? colors.planned
          : colors.pending
      const marker = L.marker([point.latitude, point.longitude], {
        icon: L.divIcon({
          className: '',
          html: `
            <div data-testid="sea-route-marker" style="display:flex;flex-direction:column;align-items:center;gap:3px;transform:translateY(-4px);">
              <div style="border-radius:999px;background:${color};box-shadow:0 6px 16px ${ROUTE_MARKER_SHADOW};color:white;font-size:11px;font-weight:800;letter-spacing:.02em;line-height:1;padding:6px 10px;white-space:nowrap;">${escapeHtml(point.mapLabel)}</div>
              <div style="height:16px;width:16px;border-radius:999px;background:${point.current ? colors.current : '#ffffff'};border:4px solid ${color};box-shadow:0 3px 10px ${ROUTE_MARKER_SHADOW};"></div>
            </div>
          `,
          iconAnchor: [36, 38],
          iconSize: [72, 48],
        }),
      }).addTo(map)
      const tooltipLines = [
        `<strong>${escapeHtml(point.mapLabel)}: ${escapeHtml(point.name)}</strong>`,
        point.current
          ? escapeHtml(
              currentPosition?.precision === 'LIVE'
                ? 'Current vessel position'
                : 'Last confirmed provider position',
            )
          : null,
        point.meta ? escapeHtml(point.meta) : null,
        point.date ? escapeHtml(formatDate(point.date)) : null,
      ]
        .filter(Boolean)
        .join('<br />')
      marker.bindTooltip(tooltipLines, {
        direction: 'top',
        offset: [0, -26],
        opacity: 0.96,
      })
    }

    const hasCurrentCoordinates =
      typeof currentPosition?.latitude === 'number' &&
      typeof currentPosition?.longitude === 'number'
    if (hasCurrentCoordinates && currentPosition) {
      const isVesselPosition = isVesselReportedPosition(currentPosition)
      const markerColor = isVesselPosition ? colors.current : colors.pending
      const markerBackground = isVesselPosition ? colors.currentSoft : '#fafafa'
      const heading =
        currentPosition.courseOverGroundDegrees != null
          ? ((currentPosition.courseOverGroundDegrees % 360) + 360) % 360
          : null
      const vesselLabel = escapeHtml(currentPosition.vessel || currentPosition.name)
      const currentMarkerHtml = isVesselPosition
        ? `
            <div
              data-testid="sea-vessel-position-marker"
              aria-label="Current position of ${vesselLabel}${heading != null ? `, heading ${Math.round(heading)} degrees` : ''}"
              style="position:relative;height:54px;width:54px;transform:translateY(-5px);"
            >
              ${
                heading != null
                  ? `<div aria-hidden="true" style="position:absolute;inset:0;transform:rotate(${heading}deg);transform-origin:center;">
                       <div style="position:absolute;left:22px;top:-7px;width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-bottom:10px solid ${colors.currentStrong};filter:drop-shadow(0 2px 2px ${ROUTE_MARKER_SHADOW});"></div>
                     </div>`
                  : ''
              }
              <div style="position:absolute;left:5px;top:5px;display:flex;height:44px;width:44px;align-items:center;justify-content:center;border:3px solid #ffffff;border-radius:999px;background:${markerColor};box-shadow:0 7px 18px ${ROUTE_MARKER_SHADOW};color:#ffffff;">
                <svg data-testid="sea-vessel-icon" aria-hidden="true" width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 10.189V14"></path>
                  <path d="M12 2v3"></path>
                  <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"></path>
                  <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-8.188-3.639a2 2 0 0 0-1.624 0L3 14a11.6 11.6 0 0 0 2.81 7.76"></path>
                  <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1s1.2 1 2.5 1c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path>
                </svg>
              </div>
            </div>
          `
        : `
            <div data-testid="sea-vessel-position-marker" style="display:flex;flex-direction:column;align-items:center;gap:3px;transform:translateY(-4px);">
              <div style="border-radius:999px;background:${markerColor};box-shadow:0 6px 16px ${ROUTE_MARKER_SHADOW};color:white;font-size:11px;font-weight:800;line-height:1;padding:6px 10px;white-space:nowrap;">LAST EVENT</div>
              <div style="height:18px;width:18px;border-radius:999px;background:${markerBackground};border:5px solid ${markerColor};box-shadow:0 3px 12px ${ROUTE_MARKER_SHADOW};"></div>
            </div>
          `
      const marker = L.marker([currentPosition.latitude!, currentPosition.longitude!], {
        zIndexOffset: 1000,
        icon: L.divIcon({
          className: '',
          html: currentMarkerHtml,
          iconAnchor: isVesselPosition ? [27, 27] : [36, 40],
          iconSize: isVesselPosition ? [54, 54] : [72, 50],
        }),
      }).addTo(map)
      marker.bindTooltip(
        [
          `<strong>${escapeHtml(currentPosition.name)}</strong>`,
          escapeHtml(getPositionLabel(currentPosition)),
          currentPosition.meta ? escapeHtml(currentPosition.meta) : null,
          currentPosition.date ? escapeHtml(formatDateTime(currentPosition.date)) : null,
          currentPosition.speedOverGroundKnots != null
            ? `${currentPosition.speedOverGroundKnots.toFixed(1)} kn`
            : null,
          currentPosition.courseOverGroundDegrees != null
            ? `Course ${Math.round(currentPosition.courseOverGroundDegrees)}°`
            : null,
        ]
          .filter(Boolean)
          .join('<br />'),
        { direction: 'top', offset: [0, -28], opacity: 0.96 },
      )
    }

    const boundsLine: L.LatLng[] =
      routeLine.length > 0
        ? routeLine
        : points.map((point) => L.latLng(point.latitude, point.longitude))
    if (hasCurrentCoordinates && currentPosition) {
      boundsLine.push(L.latLng(currentPosition.latitude!, currentPosition.longitude!))
    }

    if (boundsLine.length === 1) {
      map.setView(boundsLine[0], 6)
    } else {
      map.fitBounds(L.latLngBounds(boundsLine), {
        maxZoom: 6,
        padding: [40, 40],
      })
    }

    const resizeTimer = window.setTimeout(() => map.invalidateSize(), 0)
    return () => {
      window.clearTimeout(resizeTimer)
      map.remove()
      mapRef.current = null
    }
  }, [currentPosition, points])

  if (points.length === 0) {
    return (
      <div className="rounded-md border p-6 text-sm text-muted-foreground">
        A route was returned, but Helm does not have coordinates for the route map yet.
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-md border bg-background" data-testid="sea-route-map">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div>
          <Label className="inline-flex items-center gap-1 text-xs uppercase text-muted-foreground">
            <Navigation className="h-3.5 w-3.5" />
            Route Map
          </Label>
          <p className="mt-1 text-sm text-muted-foreground">Operational view for {reference}</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <span className="h-0.5 w-5 rounded bg-route-actual" />
            Actual
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-0.5 w-5 rounded border-t border-dashed border-route-planned" />
            Planned
          </span>
          {currentPosition && (
            <span className="inline-flex items-center gap-1">
              {isVesselReportedPosition(currentPosition) ? (
                <Ship className="h-3.5 w-3.5 text-route-current" />
              ) : (
                <span className="h-2.5 w-2.5 rounded-full bg-route-pending" />
              )}
              {getPositionLabel(currentPosition)}
            </span>
          )}
        </div>
      </div>
      <div className="relative bg-muted">
        <div
          ref={mapElementRef}
          role="img"
          aria-label={`Sea route map for ${reference}`}
          className="relative z-0 h-[420px] w-full"
          data-testid="sea-route-leaflet-map"
        />
      </div>
      {currentPosition && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t px-4 py-2 text-xs text-muted-foreground">
          {isVesselReportedPosition(currentPosition) ? (
            <Ship className="h-3.5 w-3.5 text-route-current" />
          ) : (
            <MapPin className="h-3.5 w-3.5" />
          )}
          <span className="font-medium text-foreground">
            {getPositionLabel(currentPosition)}: {currentPosition.name}
          </span>
          {currentPosition.meta && <span>· {currentPosition.meta}</span>}
          {currentPosition.date && <span>· {formatDate(currentPosition.date)}</span>}
          {currentPosition.speedOverGroundKnots != null && (
            <span>· {currentPosition.speedOverGroundKnots.toFixed(1)} kn</span>
          )}
          {currentPosition.courseOverGroundDegrees != null && (
            <span>· Course {Math.round(currentPosition.courseOverGroundDegrees)}°</span>
          )}
        </div>
      )}
    </div>
  )
}

function SeaRouteMilestones({
  routeSteps,
  containers,
}: {
  routeSteps: SeaRouteStep[]
  containers: SeaTrackingContainer[]
}) {
  const points = buildRouteProgressSteps(routeSteps, containers)
  return (
    <div className="rounded-md border p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <Label className="inline-flex items-center gap-1 text-xs uppercase text-muted-foreground">
            <Route className="h-3.5 w-3.5" />
            Route
          </Label>
          <p className="mt-1 text-sm text-muted-foreground">
            Origin, transshipment, arrival, and delivery milestones.
          </p>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-5">
        {points.map((step, index) => (
          <div
            key={`${step.label}-${step.name}-${index}`}
            className="relative rounded-md border bg-background p-3"
          >
            <div className="mb-3 h-1 rounded-full bg-muted">
              <div
                className={`h-1 rounded-full ${step.completed ? 'bg-route-actual' : 'bg-muted'}`}
              />
            </div>
            <div className="mb-2 flex items-center gap-2">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold ${step.current ? 'bg-route-current text-white' : step.completed ? 'bg-route-actual text-white' : 'border text-muted-foreground'}`}
              >
                {index + 1}
              </span>
              <span className="text-xs font-medium uppercase text-muted-foreground">
                {step.label}
              </span>
            </div>
            <p className="font-medium">{step.name}</p>
            {step.meta && <p className="mt-1 text-xs text-muted-foreground">{step.meta}</p>}
            {step.date && (
              <p className="mt-2 text-xs text-muted-foreground">{formatDate(step.date)}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function SeaMovementsTable({
  movements,
  containers,
}: {
  movements: SeaMovement[]
  containers: SeaTrackingContainer[]
}) {
  const terminalRows = containers.filter(
    (container) =>
      container.pod_terminal_name || container.pod_terminal_unlocode || container.empty_return,
  )
  const railMovements = movements.filter((movement) => movement.mode.toLowerCase().includes('rail'))

  return (
    <section className="space-y-3 rounded-md border p-4" data-testid="sea-movement-history">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Label className="inline-flex items-center gap-1 text-xs uppercase text-muted-foreground">
            <History className="h-3.5 w-3.5" />
            Container Movement History
          </Label>
          <p className="mt-1 text-sm text-muted-foreground">
            {movements.length > 0
              ? `${movements.length.toLocaleString()} movement events mirrored into Helm.`
              : 'No movement events have been returned yet.'}
          </p>
        </div>
        <Badge variant="outline">{movements.length.toLocaleString()} events</Badge>
      </div>
      <Tabs defaultValue="movements" className="space-y-3">
        <TabsList>
          <TabsTrigger value="movements">
            <History className="h-4 w-4" /> Movements
          </TabsTrigger>
          <TabsTrigger value="terminal">
            <Anchor className="h-4 w-4" /> Terminal Data
          </TabsTrigger>
          <TabsTrigger value="rail">
            <Truck className="h-4 w-4" /> Rail Data
          </TabsTrigger>
        </TabsList>
        <TabsContent value="movements" className="mt-0">
          <div className="overflow-hidden rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Transport Mode</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.length > 0 ? (
                  movements.map((movement) => {
                    const ModeIcon = modeIcon(movement.mode)
                    return (
                      <TableRow key={movement.key} data-testid="sea-movement-row">
                        <TableCell className="min-w-40">
                          <div className="font-medium">
                            {movement.date ? formatDate(movement.date) : 'Pending'}
                          </div>
                          {movement.date && (
                            <div className="text-xs text-muted-foreground">
                              {formatDateTime(movement.date)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{movement.location}</TableCell>
                        <TableCell>
                          <div className="font-medium">{movement.event}</div>
                          {movement.vessel && (
                            <div className="text-xs text-muted-foreground">{movement.vessel}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-2">
                            <ModeIcon className="h-4 w-4 text-muted-foreground" />
                            {movement.mode}
                          </span>
                        </TableCell>
                      </TableRow>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-muted-foreground">
                      No movement rows have been returned yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="terminal" className="mt-0">
          <div className="overflow-hidden rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Container</TableHead>
                  <TableHead>POD Terminal</TableHead>
                  <TableHead>UN/LOCODE</TableHead>
                  <TableHead>Empty Return</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {terminalRows.length > 0 ? (
                  terminalRows.map((container) => (
                    <TableRow
                      key={container.container_number || container.shipment_reference || 'terminal'}
                    >
                      <TableCell className="font-medium">
                        {container.container_number ||
                          container.shipment_reference ||
                          'Container pending'}
                      </TableCell>
                      <TableCell>
                        {container.pod_terminal_name ||
                          readString(container.pod, 'name') ||
                          'Not set'}
                      </TableCell>
                      <TableCell>
                        {container.pod_terminal_unlocode ||
                          readString(container.pod, 'unlocode') ||
                          'Not set'}
                      </TableCell>
                      <TableCell>
                        {readString(container.empty_return, 'name') ||
                          readString(container.empty_return, 'location') ||
                          'Not returned'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-muted-foreground">
                      No terminal rows have been returned yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="rail" className="mt-0">
          <div className="overflow-hidden rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Event</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {railMovements.length > 0 ? (
                  railMovements.map((movement) => (
                    <TableRow key={`rail-${movement.key}`}>
                      <TableCell>
                        {movement.date ? formatDateTime(movement.date) : 'Pending'}
                      </TableCell>
                      <TableCell>{movement.location}</TableCell>
                      <TableCell>{movement.event}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="text-muted-foreground">
                      No rail movements have been returned yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </section>
  )
}

function TaxAmountRow({
  label,
  value,
  currency = 'UGX',
}: {
  label: string
  value: number | null | undefined
  currency?: string | null
}) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{formatCurrency(value, currency || 'UGX')}</span>
    </div>
  )
}

function EditConsignmentDialog({
  consignment,
  open,
  onOpenChange,
  onSaved,
}: {
  consignment: ConsignmentDetail
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const tasks = consignment.tasks ?? []
  const activeTask =
    tasks.find((task) => task.status === 'IN_PROGRESS') ??
    tasks.find((task) => task.status === 'TODO') ??
    null
  const [destinationAddress, setDestinationAddress] = useState(
    consignment.destination_address || consignment.destination_port || '',
  )
  const shippingMethod = consignment.shipping_method || 'AIR'
  const [airService, setAirService] = useState<HelmAirService | ''>(consignment.air_service ?? '')
  const [serviceTypes, setServiceTypes] = useState<HelmServiceType[]>(
    consignment.service_types?.length
      ? consignment.service_types
      : [consignment.service_type || 'END_TO_END_IMPORT'],
  )
  const [workflowPhase, setWorkflowPhase] = useState<ConsignmentWorkflowPhase>(
    consignment.workflow_phase,
  )
  const [operationalState, setOperationalState] = useState<WorkflowOperationalState>(
    consignment.operational_state,
  )
  const [ownerUserId, setOwnerUserId] = useState(consignment.owner_user_id || '')
  const [nextAction, setNextAction] = useState(activeTask?.title || consignment.next_action || '')
  const [taskStatus, setTaskStatus] = useState<WorkflowTaskStatus>(activeTask?.status ?? 'TODO')
  const [taskDueAt, setTaskDueAt] = useState(toDateTimeLocal(activeTask?.due_at))
  const [transitionComment, setTransitionComment] = useState('')
  const [communicationChannel, setCommunicationChannel] = useState<HelmCommunicationChannel>(
    consignment.preferred_communication_channel || 'EMAIL',
  )
  const [serviceNotes, setServiceNotes] = useState(consignment.service_notes || '')
  // The air or sea leg's reference; saving a package's courier number here would make it the consignment's.
  const [trackingNumber, setTrackingNumber] = useState(
    consignment.international_tracking_number || consignment.tracking_number || '',
  )
  const [status, setStatus] = useState(consignment.status)
  const [visibility, setVisibility] = useState(consignment.visibility || 'NOT_READY')
  const [paymentStatus, setPaymentStatus] = useState(consignment.payment_status || 'PENDING')
  const [insuranceOpted, setInsuranceOpted] = useState(
    Boolean(consignment.insurance_opted ?? consignment.insurance),
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)

    if (!destinationAddress.trim()) {
      setError('Destination address is required')
      setSubmitting(false)
      return
    }

    try {
      await apiClient.updateConsignment(consignment.id, {
        destination_address: destinationAddress.trim(),
        ...(shippingMethod === 'AIR' && airService !== (consignment.air_service ?? '')
          ? { air_service: airService || null }
          : {}),
        service_type: serviceTypes[0],
        service_types: serviceTypes,
        workflow_phase: workflowPhase,
        operational_state: operationalState,
        owner_user_id: ownerUserId.trim() || null,
        next_task: nextAction.trim()
          ? {
              id: activeTask?.id,
              title: nextAction.trim(),
              status: taskStatus,
              priority: activeTask?.priority ?? 'NORMAL',
              assignee_user_id: ownerUserId.trim() || null,
              due_at: taskDueAt ? new Date(taskDueAt).toISOString() : null,
            }
          : null,
        transition_comment: transitionComment.trim() || null,
        preferred_communication_channel: communicationChannel,
        service_notes: serviceNotes.trim() || null,
        tracking_number: trackingNumber.trim() || null,
        status,
        visibility: visibility as UpdateConsignmentRequest['visibility'],
        payment_status: paymentStatus as UpdateConsignmentRequest['payment_status'],
        insurance_opted: insuranceOpted,
      })
      onOpenChange(false)
      onSaved()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update consignment')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Consignment</DialogTitle>
          <DialogDescription>
            Update shipment workflow, tracking, and payment fields.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="destination-address">Destination</Label>
            <Textarea
              id="destination-address"
              value={destinationAddress}
              onChange={(event) => setDestinationAddress(event.target.value)}
              disabled={submitting}
              rows={3}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="service-types">Service Types</Label>
              <ServiceTypeMultiSelect
                id="service-types"
                value={serviceTypes}
                onValueChange={setServiceTypes}
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <HintedLabel plain htmlFor="workflow-phase" hint={FIELD_HELP.consignmentPhase}>
                Workflow Phase
              </HintedLabel>
              <Select
                value={workflowPhase}
                onValueChange={(value) => setWorkflowPhase(value as ConsignmentWorkflowPhase)}
                disabled={submitting}
              >
                <SelectTrigger id="workflow-phase">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[workflowPhase, ...consignmentPhaseTransitions[consignment.workflow_phase]]
                    .filter((option, index, values) => values.indexOf(option) === index)
                    .map((option) => (
                      <SelectItem key={option} value={option}>
                        {formatLabel(option)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <HintedLabel plain htmlFor="workflow-state" hint={FIELD_HELP.operationalState}>
                Operating State
              </HintedLabel>
              <Select
                value={operationalState}
                onValueChange={(value) => setOperationalState(value as WorkflowOperationalState)}
                disabled={submitting}
              >
                <SelectTrigger id="workflow-state">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[operationalState, ...operationalStateTransitions[consignment.operational_state]]
                    .filter((option, index, values) => values.indexOf(option) === index)
                    .map((option) => (
                      <SelectItem key={option} value={option}>
                        {formatLabel(option)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="communication-channel">Preferred Channel</Label>
              <Select
                value={communicationChannel}
                onValueChange={(value) =>
                  setCommunicationChannel(value as HelmCommunicationChannel)
                }
                disabled={submitting}
              >
                <SelectTrigger id="communication-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {communicationChannelOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {formatLabel(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="owner-user-id">Owner</Label>
              <HelmOwnerCombobox
                id="owner-user-id"
                value={ownerUserId}
                onValueChange={setOwnerUserId}
                disabled={submitting}
                placeholder="Search owner by email"
              />
            </div>

            <div className="space-y-2">
              <HintedLabel plain htmlFor="next-action" hint={FIELD_HELP.nextTask}>
                Next Task
              </HintedLabel>
              <Input
                id="next-action"
                value={nextAction}
                onChange={(event) => setNextAction(event.target.value)}
                disabled={submitting}
                placeholder="Quote, chase invoice, confirm PVoC, clear goods..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-status">Task Status</Label>
              <Select
                value={taskStatus}
                onValueChange={(value) => setTaskStatus(value as WorkflowTaskStatus)}
                disabled={submitting}
              >
                <SelectTrigger id="task-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(['TODO', 'IN_PROGRESS', 'DONE', 'CANCELLED'] as WorkflowTaskStatus[]).map(
                    (option) => (
                      <SelectItem key={option} value={option}>
                        {formatLabel(option)}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-due-at">Task Due</Label>
              <Input
                id="task-due-at"
                type="datetime-local"
                value={taskDueAt}
                onChange={(event) => setTaskDueAt(event.target.value)}
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="transition-comment">Update Comment</Label>
              <Input
                id="transition-comment"
                value={transitionComment}
                onChange={(event) => setTransitionComment(event.target.value)}
                disabled={submitting}
                placeholder="Why did this work change?"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="shipping-method">Shipping Method</Label>
              <Input id="shipping-method" value={shipmentLabel(shippingMethod, null)} disabled />
              <p className="text-xs text-muted-foreground">
                Shipping method is fixed when the operational consignment code is assigned.
              </p>
            </div>

            {shippingMethod === 'AIR' && (
              <div className="space-y-2">
                <HintedLabel plain htmlFor="air-service" hint={FIELD_HELP.airService}>
                  Air Service
                </HintedLabel>
                <EnumSelect
                  value={airService}
                  onValueChange={(value) => setAirService(value as HelmAirService)}
                  options={AIR_SERVICES}
                  placeholder="Freight, express, courier or charter"
                  disabled={submitting}
                  triggerProps={{ id: 'air-service' }}
                />
              </div>
            )}

            <div className="space-y-2">
              <HintedLabel plain htmlFor="tracking-number" hint={FIELD_HELP.trackingNumber}>
                Tracking Number
              </HintedLabel>
              <Input
                id="tracking-number"
                value={trackingNumber}
                onChange={(event) => setTrackingNumber(event.target.value)}
                disabled={submitting}
                placeholder="B/L, air waybill or container"
              />
            </div>

            <div className="space-y-2">
              <HintedLabel plain htmlFor="status" hint={FIELD_HELP.consignmentStatus}>
                Status
              </HintedLabel>
              <Select
                value={status}
                onValueChange={(value) => setStatus(value as Consignment['status'])}
                disabled={submitting}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {formatLabel(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="visibility">Visibility</Label>
              <Select
                value={visibility}
                onValueChange={(value) =>
                  setVisibility(value as NonNullable<UpdateConsignmentRequest['visibility']>)
                }
                disabled={submitting}
              >
                <SelectTrigger id="visibility">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NOT_READY">Not Ready</SelectItem>
                  <SelectItem value="READY_FOR_SHIPMENT">Ready For Shipment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <HintedLabel plain htmlFor="payment-status" hint={FIELD_HELP.paymentStatus}>
                Payment Status
              </HintedLabel>
              <Select value={paymentStatus} onValueChange={setPaymentStatus} disabled={submitting}>
                <SelectTrigger id="payment-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="INVOICED">Invoiced</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
              <input
                type="checkbox"
                checked={insuranceOpted}
                onChange={(event) => setInsuranceOpted(event.target.checked)}
                disabled={submitting}
                className="h-4 w-4"
              />
              Insurance opted
            </label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="service-notes">Service Notes</Label>
            <Textarea
              id="service-notes"
              value={serviceNotes}
              onChange={(event) => setServiceNotes(event.target.value)}
              disabled={submitting}
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function buildCustomerUpdateMessage(consignment: ConsignmentDetail) {
  // Named by its reference and title; tracked by its B/L or air waybill only.
  const tracking = shipmentTracking(consignment)
  const status = formatLabel(consignment.status)
  const nextAction = consignment.next_action ? `\nNext step: ${consignment.next_action}` : ''
  const services = (
    consignment.service_types?.length ? consignment.service_types : [consignment.service_type]
  )
    .map(formatLabel)
    .join(', ')
  return [
    customerGreeting(consignment.customer),
    '',
    `Update on your Helm ${services.toLowerCase()} request: ${status}.`,
    `Shipment: ${shipmentReference(consignment)}.`,
    tracking ? `Tracking: ${tracking}.` : null,
    consignment.destination_address ? `Destination: ${consignment.destination_address}.` : null,
    nextAction.trim() || null,
    '',
    'Regards,',
    'Xtellar Helm',
  ]
    .filter(Boolean)
    .join('\n')
}

function CommunicationsPanel({
  consignment,
  communications,
  isAdmin,
  onChanged,
}: {
  consignment: ConsignmentDetail
  communications: HelmCommunication[]
  isAdmin: boolean
  onChanged: () => void
}) {
  const customerEmail = consignment.customer?.email || ''
  const whatsappPhone = normalizeWhatsAppPhone(consignment.customer?.phone)
  const defaultSubject = `Helm update for your shipment ${shipmentReference(consignment)}`
  const defaultMessage = buildCustomerUpdateMessage(consignment)
  const [emailSubject, setEmailSubject] = useState(defaultSubject)
  const [emailBody, setEmailBody] = useState(defaultMessage)
  const [ccRecipients, setCcRecipients] = useState<string[]>([])
  const [attachments, setAttachments] = useState<HelmOutboundCommunicationAttachment[]>([])
  const [uploadingAttachments, setUploadingAttachments] = useState(false)
  const [whatsAppBody, setWhatsAppBody] = useState(defaultMessage)
  const [submitting, setSubmitting] = useState<'EMAIL' | 'WHATSAPP' | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleQueueEmail(event: FormEvent) {
    event.preventDefault()
    if (!customerEmail) {
      setError('Customer email is not set')
      return
    }

    setSubmitting('EMAIL')
    setError(null)
    try {
      await apiClient.createCommunication({
        customer_id: consignment.customer_id,
        consignment_id: consignment.id,
        channel: 'EMAIL',
        template_key: 'HELM_STATUS_UPDATE',
        recipient: customerEmail,
        cc: ccRecipients,
        subject: emailSubject.trim() || defaultSubject,
        body: emailBody.trim(),
        attachments,
        send: true,
        metadata: {
          status: consignment.status,
          inquiry_stage: consignment.inquiry_stage,
          service_type: consignment.service_type,
          service_types: consignment.service_types,
        },
      })
      setAttachments([])
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to send email update')
    } finally {
      setSubmitting(null)
    }
  }

  async function handleOpenWhatsApp() {
    if (!whatsappPhone) {
      setError('Customer phone is not set')
      return
    }

    setSubmitting('WHATSAPP')
    setError(null)
    const body = whatsAppBody.trim() || defaultMessage
    try {
      await apiClient.createCommunication({
        customer_id: consignment.customer_id,
        consignment_id: consignment.id,
        channel: 'WHATSAPP',
        status: 'LOGGED',
        template_key: 'HELM_WHATSAPP_STATUS_UPDATE',
        recipient: `+${whatsappPhone}`,
        body,
        metadata: {
          status: consignment.status,
          inquiry_stage: consignment.inquiry_stage,
          service_type: consignment.service_type,
          service_types: consignment.service_types,
        },
      })
      window.open(
        `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(body)}`,
        '_blank',
        'noopener,noreferrer',
      )
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to log WhatsApp contact')
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Communications</CardTitle>
        <CardDescription>
          Email queue and WhatsApp contact history for this inquiry.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Preferred Channel</Label>
            <p className="mt-1 font-medium">
              {formatLabel(consignment.preferred_communication_channel)}
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Last Communication</Label>
            <p className="mt-1 font-medium">{formatDateTime(consignment.last_communication_at)}</p>
          </div>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Next Action</Label>
            <p className="mt-1 font-medium">{consignment.next_action || 'Not set'}</p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isAdmin ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <form onSubmit={handleQueueEmail} className="space-y-3 rounded-md border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">Email Update</div>
                  <div className="text-sm text-muted-foreground">
                    {customerEmail || 'No email on customer'}
                  </div>
                </div>
                <Button
                  type="submit"
                  size="sm"
                  disabled={submitting !== null || uploadingAttachments || !customerEmail}
                >
                  {submitting === 'EMAIL' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  Send
                </Button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-subject">Subject</Label>
                <Input
                  id="email-subject"
                  value={emailSubject}
                  onChange={(event) => setEmailSubject(event.target.value)}
                  disabled={submitting !== null}
                />
              </div>
              <HelmCcSelector
                value={ccRecipients}
                onChange={setCcRecipients}
                disabled={submitting !== null}
                excludeEmails={[customerEmail]}
              />
              <div className="space-y-2">
                <Label htmlFor="email-body">Message</Label>
                <Textarea
                  id="email-body"
                  value={emailBody}
                  onChange={(event) => setEmailBody(event.target.value)}
                  disabled={submitting !== null}
                  rows={7}
                />
              </div>
              <CommunicationAttachmentPicker
                value={attachments}
                onChange={setAttachments}
                onError={setError}
                onUploadingChange={setUploadingAttachments}
                disabled={submitting !== null}
              />
            </form>

            <div className="space-y-3 rounded-md border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">WhatsApp</div>
                  <div className="text-sm text-muted-foreground">
                    {consignment.customer?.phone || 'No phone on customer'}
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleOpenWhatsApp}
                  disabled={submitting !== null || !whatsappPhone}
                >
                  {submitting === 'WHATSAPP' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MessageSquare className="h-4 w-4" />
                  )}
                  Open
                </Button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp-body">Message</Label>
                <Textarea
                  id="whatsapp-body"
                  value={whatsAppBody}
                  onChange={(event) => setWhatsAppBody(event.target.value)}
                  disabled={submitting !== null}
                  rows={9}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-md border p-4 text-sm text-muted-foreground">
            Admin access is required to queue or log customer communications.
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium">History</h3>
            <Badge variant="outline">{communications.length}</Badge>
          </div>
          {communications.length === 0 ? (
            <p className="rounded-md border p-4 text-sm text-muted-foreground">
              No communication events have been recorded for this inquiry.
            </p>
          ) : (
            <div className="divide-y rounded-md border">
              {communications.map((communication) => {
                const cc = communicationCcRecipients(communication)
                return (
                  <div key={communication.id} className="space-y-2 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant={communication.channel === 'WHATSAPP' ? 'secondary' : 'outline'}
                      >
                        {formatLabel(communication.channel)}
                      </Badge>
                      <Badge variant="outline">{formatLabel(communication.status)}</Badge>
                      <span className="text-sm text-muted-foreground">
                        {formatDateTime(communication.sent_at || communication.created_at)}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">{communication.recipient}</div>
                    {cc.length > 0 && (
                      <div className="text-xs text-muted-foreground">CC: {cc.join(', ')}</div>
                    )}
                    <TranslatableMessage
                      subject={communication.subject}
                      body={communication.body}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {consignment.service_notes && (
          <div className="rounded-md border p-4">
            <Label className="text-xs uppercase text-muted-foreground">Service Notes</Label>
            <Translatable
              text={consignment.service_notes}
              context="note"
              className="mt-2 whitespace-pre-wrap text-sm"
              label="the service notes"
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const fallbackDocumentDefinitions: ConsignmentDocumentDefinition[] = [
  {
    type: 'COMMERCIAL_INVOICE',
    label: 'Commercial invoice',
    capability: 'GENERATED_OR_UPLOAD',
    description: 'Customer-facing invoice for the goods and services in this consignment.',
    defaultIssuer: 'XTELLAR',
  },
  {
    type: 'RECEIPT',
    label: 'Receipt',
    capability: 'GENERATED_OR_UPLOAD',
    description: 'Payment receipt issued for the consignment.',
    defaultIssuer: 'XTELLAR',
  },
  {
    type: 'PACKING_LIST',
    label: 'Packing list',
    capability: 'GENERATED_OR_UPLOAD',
    description: 'Item quantities, packaging, weights, and measurements.',
    defaultIssuer: 'SUPPLIER',
  },
  {
    type: 'DELIVERY_NOTE',
    label: 'Delivery note',
    capability: 'GENERATED_OR_UPLOAD',
    description: 'Delivery handoff record for the consignee.',
    defaultIssuer: 'XTELLAR',
  },
  {
    type: 'GOX_SHIPPING_INVOICE',
    label: 'GOX air shipping invoice',
    capability: 'GENERATED_OR_UPLOAD',
    description: 'Air shipping invoice used for GOX forwarding workflows.',
    defaultIssuer: 'XTELLAR',
  },
  {
    type: 'GOX_BOOKING_NOTE',
    label: 'Booking note',
    capability: 'GENERATED',
    description: 'Booking instruction prepared from shipment, party, and cargo details.',
    defaultIssuer: 'XTELLAR',
  },
  ...(
    [
      [
        'ORIGINAL_INVOICE',
        'Original invoice',
        'Invoice supplied by the customer or supplier.',
        'CUSTOMER',
      ],
      [
        'PROOF_OF_PAYMENT',
        'Proof of payment',
        'Evidence of payment for the consignment.',
        'CUSTOMER',
      ],
      ['BILL_OF_LADING', 'Bill of lading', 'Carrier-issued sea transport document.', 'CARRIER'],
      ['AIRWAY_BILL', 'Air waybill', 'Carrier-issued air transport document.', 'CARRIER'],
      [
        'CUSTOMS_DECLARATION',
        'Customs declaration',
        'Declaration accepted by the customs authority.',
        'AUTHORITY',
      ],
      [
        'PVOC_CERTIFICATE',
        'PVoC certificate',
        'Certificate issued by the inspection company.',
        'AUTHORITY',
      ],
      [
        'PRODUCT_TEST_REPORT',
        'Product test report',
        'Testing report issued by the laboratory or authority.',
        'AUTHORITY',
      ],
      [
        'CLEARANCE_RELEASE',
        'Clearance release',
        'Release evidence issued after customs clearance.',
        'AUTHORITY',
      ],
    ] as const
  ).map(([type, label, description, defaultIssuer]) => ({
    type,
    label,
    description,
    defaultIssuer,
    capability: 'UPLOAD_ONLY' as const,
  })),
]

const consignmentTabValues = [
  'items',
  'tax',
  'documents',
  'tracking',
  'communications',
  'customer',
] as const

type ConsignmentTabValue = (typeof consignmentTabValues)[number]

function isConsignmentTabValue(value: string | undefined): value is ConsignmentTabValue {
  return consignmentTabValues.includes(value as ConsignmentTabValue)
}

const editableItemFields = [
  'description',
  'quantity',
  'unit_price',
  'product_use',
  'material',
  'hs_code_china',
  'hs_code_uganda',
  'tracking_number',
  'is_special_item',
  'special_item_type',
] as const satisfies ReadonlyArray<keyof ConsignmentItem>

/** Each item as last saved, so a reload still in flight cannot undo a save. */
function latestItems(served: ConsignmentItem[], shown: ConsignmentItem[]) {
  return served.map((item) => {
    const local = shown.find((candidate) => candidate.id === item.id)
    return local && Date.parse(local.updated_at) > Date.parse(item.updated_at) ? local : item
  })
}

/** The latest saved item with the fields this person changed laid back on top. */
function keepEdits(draft: ConsignmentItem, opened: ConsignmentItem, latest: ConsignmentItem) {
  const rebased: ConsignmentItem = { ...latest }
  for (const field of editableItemFields) {
    if (draft[field] !== opened[field]) Object.assign(rebased, { [field]: draft[field] })
  }
  return rebased
}

function isItemConflict(error: unknown) {
  return error instanceof ApiClientError && error.status === 409
}

/** The AI's code is not one exact code in the tariff schedule, so a person has to pick it. */
function isUnmatchedClassification(error: unknown) {
  return error instanceof ApiClientError && error.status === 422
}

function listNames(names: string[]) {
  return new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(names)
}

function classificationSummary(
  classified: string[],
  toReview: string[],
  unmatched: string[],
  changed: string[],
) {
  const sentences: string[] = []
  if (classified.length > 0) {
    sentences.push(`Classified ${classified.length} item${classified.length === 1 ? '' : 's'}.`)
  }
  if (toReview.length > 0) {
    sentences.push(
      toReview.length === 1
        ? `1 code isn't in the tariff schedule and needs review: ${listNames(toReview)}.`
        : `${toReview.length} codes aren't in the tariff schedule and need review: ${listNames(toReview)}.`,
    )
  }
  if (unmatched.length > 0) {
    sentences.push(
      `Helm couldn't match ${listNames(unmatched)} to a code in the tariff schedule. Enter ${
        unmatched.length === 1 ? 'its HS code' : 'their HS codes'
      } by hand.`,
    )
  }
  if (changed.length > 0) {
    sentences.push(
      `Someone else changed ${listNames(changed)} while Helm was classifying, so ${
        changed.length === 1 ? 'its code was' : 'their codes were'
      } not saved. The latest version is now shown.`,
    )
  }
  return sentences.length > 0 ? sentences.join(' ') : null
}

function taxReviewWarning(names: string[]) {
  return names.length === 1
    ? `Tax leaves out ${names[0]}: its HS code isn't in the tariff schedule and needs review. Confirm it on the Items tab to include it.`
    : `Tax leaves out ${listNames(names)}: their HS codes aren't in the tariff schedule and need review. Confirm them on the Items tab to include them.`
}

function EditableItemsPanel({
  consignment,
  isAdmin,
  onChanged,
}: {
  consignment: ConsignmentDetail
  isAdmin: boolean
  onChanged: () => void
}) {
  const [items, setItems] = useState<ConsignmentItem[]>(consignment.items)
  // Each item's package on its way to our China warehouse, for its badge.
  const packageByItem = useMemo(
    () =>
      // `Map` here is the lucide icon; the collection is the global one.
      new globalThis.Map(
        (consignment.domestic_gathering?.packages ?? []).flatMap((pkg) =>
          pkg.items.map((item) => [item.id, pkg] as const),
        ),
      ),
    [consignment.domestic_gathering],
  )
  const [expandedItemIds, setExpandedItemIds] = useState<string[]>([])
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [draftItem, setDraftItem] = useState<ConsignmentItem | null>(null)
  const [savingItemId, setSavingItemId] = useState<string | null>(null)
  const [classifying, setClassifying] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!editingItemId) {
      setItems((current) => latestItems(consignment.items, current))
    }
  }, [consignment.items, editingItemId])

  function patchItem(itemId: string, patch: Partial<ConsignmentItem>) {
    setItems((current) =>
      current.map((item) => (item.id === itemId ? { ...item, ...patch } : item)),
    )
  }

  function patchDraft(patch: Partial<ConsignmentItem>) {
    setDraftItem((current) => (current ? { ...current, ...patch } : current))
  }

  function toggleExpanded(itemId: string) {
    setExpandedItemIds((current) =>
      current.includes(itemId)
        ? current.filter((expandedItemId) => expandedItemId !== itemId)
        : [...current, itemId],
    )
  }

  function startEditing(item: ConsignmentItem) {
    if (!isAdmin || savingItemId || classifying || editingItemId) return
    setEditingItemId(item.id)
    setDraftItem({ ...item })
    setExpandedItemIds((current) => (current.includes(item.id) ? current : [...current, item.id]))
    setError(null)
    setMessage(null)
  }

  function cancelEditing() {
    setEditingItemId(null)
    setDraftItem(null)
  }

  async function saveItem(item: ConsignmentItem) {
    const original = items.find((current) => current.id === item.id)
    if (!original) {
      setError('This item is no longer available. Reload the consignment and try again.')
      return
    }

    const request: UpdateConsignmentItemRequest = {
      expected_updated_at: original.updated_at,
    }
    const description = item.description || item.name
    if (description !== (original.description || original.name)) request.description = description
    if (item.quantity !== original.quantity) request.quantity = item.quantity
    if (item.unit_price !== original.unit_price) request.unit_price = item.unit_price
    if ((item.product_use || null) !== (original.product_use || null)) {
      request.product_use = item.product_use || null
    }
    if ((item.material || null) !== (original.material || null)) {
      request.material = item.material || null
    }
    if ((item.hs_code_china || null) !== (original.hs_code_china || null)) {
      request.hs_code_china = item.hs_code_china || null
    }
    if ((item.hs_code_uganda || null) !== (original.hs_code_uganda || null)) {
      request.hs_code_uganda = item.hs_code_uganda || null
    }
    if ((item.tracking_number || null) !== (original.tracking_number || null)) {
      request.tracking_number = item.tracking_number || null
    }
    if (Boolean(item.is_special_item) !== Boolean(original.is_special_item)) {
      request.is_special_item = Boolean(item.is_special_item)
    }
    if ((item.special_item_type || null) !== (original.special_item_type || null)) {
      request.special_item_type = item.special_item_type || null
    }
    // Saving the item with its code is how a person confirms an AI code.
    if (original.hs_code_needs_review && item.hs_code_uganda) {
      request.hs_code_needs_review = false
    }

    if (Object.keys(request).length === 1) {
      setEditingItemId(null)
      setDraftItem(null)
      return
    }

    setSavingItemId(item.id)
    setError(null)
    setMessage(null)
    try {
      const updated = await apiClient.updateConsignmentItem(consignment.id, item.id, request)
      patchItem(item.id, updated)
      setMessage(`Saved ${updated.name}`)
      setEditingItemId(null)
      setDraftItem(null)
      onChanged()
    } catch (error) {
      if (isItemConflict(error)) {
        await keepEditsOnLatest(item, original)
      } else {
        setError(error instanceof Error ? error.message : 'Failed to save item')
      }
    } finally {
      setSavingItemId(null)
    }
  }

  async function reloadItems() {
    const latest = await apiClient.getConsignment(consignment.id)
    setItems((current) => latestItems(latest.items, current))
    return latest.items
  }

  /** Someone saved the item first: show their version, keep this person's edits on it. */
  async function keepEditsOnLatest(draft: ConsignmentItem, opened: ConsignmentItem) {
    try {
      const latest = (await reloadItems()).find((current) => current.id === draft.id)
      if (!latest) {
        setEditingItemId(null)
        setDraftItem(null)
        setError(`${opened.name} was removed from this consignment while you were editing it.`)
      } else {
        setDraftItem(keepEdits(draft, opened, latest))
        setError(
          `Someone else saved changes to ${opened.name} while you were editing it. Their changes are now shown with your edits on top; check them and save again.`,
        )
      }
      onChanged()
    } catch {
      setError(
        `Someone else saved changes to ${opened.name} while you were editing it. Reload the page to see them, then make your change again.`,
      )
    }
  }

  async function confirmCode(item: ConsignmentItem) {
    if (savingItemId || classifying || editingItemId) return

    setSavingItemId(item.id)
    setError(null)
    setMessage(null)
    try {
      const updated = await apiClient.updateConsignmentItem(consignment.id, item.id, {
        expected_updated_at: item.updated_at,
        hs_code_needs_review: false,
      })
      patchItem(item.id, updated)
      setMessage(`Confirmed HS code ${updated.hs_code_uganda} for ${updated.name}.`)
      onChanged()
    } catch (error) {
      if (isItemConflict(error)) {
        await reloadItems().catch(() => undefined)
        setError(
          `Someone else changed ${item.name} first, so its code wasn't confirmed. Check the latest version and confirm it again.`,
        )
        onChanged()
      } else {
        setError(error instanceof Error ? error.message : 'Failed to confirm the HS code')
      }
    } finally {
      setSavingItemId(null)
    }
  }

  async function classifyMissing() {
    if (editingItemId || savingItemId || classifying) return

    const targets = items.filter((item) => (item.description || item.name) && !item.hs_code_uganda)
    if (targets.length === 0) {
      setMessage('All items already have Uganda HS codes.')
      return
    }

    setClassifying(true)
    setError(null)
    setMessage(null)
    const classified: string[] = []
    const toReview: string[] = []
    const unmatched: string[] = []
    const changed: string[] = []
    try {
      for (const item of targets) {
        let result: ClassifyResponse
        try {
          result = await apiClient.classify({
            description: item.description || item.name,
            include_unmatched: true,
          })
        } catch (error) {
          // An API older than include_unmatched still refuses the code outright.
          if (!isUnmatchedClassification(error)) throw error
          unmatched.push(item.name)
          continue
        }

        const needsReview = result.schedule_match === false
        try {
          const updated = await apiClient.updateConsignmentItem(consignment.id, item.id, {
            expected_updated_at: item.updated_at,
            hs_code_uganda: result.hs_code,
            ai_confidence: result.confidence,
            ...(needsReview ? { hs_code_needs_review: true } : {}),
          })
          patchItem(item.id, updated)
          classified.push(item.name)
          if (needsReview) toReview.push(item.name)
        } catch (error) {
          if (!isItemConflict(error)) throw error
          changed.push(item.name)
        }
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to classify items')
    } finally {
      setMessage(classificationSummary(classified, toReview, unmatched, changed))
      // The reload also brings in the versions someone else saved.
      if (classified.length > 0 || changed.length > 0) onChanged()
      setClassifying(false)
    }
  }

  const totalValue = items.reduce(
    (sum, item) => sum + (item.total_value ?? item.quantity * item.unit_price),
    0,
  )

  function handleRowDoubleClick(event: React.MouseEvent<HTMLElement>, item: ConsignmentItem) {
    if ((event.target as HTMLElement).closest('button, a, input, textarea, select, label')) return
    startEditing(item)
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Consignment Items</CardTitle>
            <CardDescription>
              {items.length} line items, {formatCurrency(totalValue)}. Expand an item for
              operational details{isAdmin ? ', or double-click it to edit' : ''}.
            </CardDescription>
          </div>
          {isAdmin && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={classifyMissing}
              disabled={
                classifying || items.length === 0 || Boolean(editingItemId) || Boolean(savingItemId)
              }
            >
              {classifying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Classify Missing
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {message && (
          <Alert>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        {items.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">
            No items have been recorded for this consignment.
          </p>
        ) : (
          <div
            className="divide-y overflow-hidden rounded-md border"
            data-testid="consignment-items"
          >
            {items.map((item) => {
              const isExpanded = expandedItemIds.includes(item.id)
              const isEditing = editingItemId === item.id && draftItem?.id === item.id
              const description = item.description || item.name
              const unitTotal = item.quantity * item.unit_price

              return (
                <Translated
                  key={item.id}
                  texts={[item.simple_name || item.name, description]}
                  context="item"
                >
                  {(translated, observe) => (
                    <article
                      ref={observe}
                      className="bg-background"
                      data-testid={`consignment-item-${item.id}`}
                      onDoubleClick={(event) => handleRowDoubleClick(event, item)}
                    >
                      <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-3 p-3 sm:p-4 lg:grid-cols-[auto_minmax(0,1fr)_minmax(21rem,auto)_auto] lg:items-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="-ml-1"
                          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.name} details`}
                          aria-expanded={isExpanded}
                          aria-controls={`consignment-item-details-${item.id}`}
                          disabled={isEditing}
                          onClick={() => toggleExpanded(item.id)}
                        >
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </Button>

                        <div className="min-w-0">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <p className="truncate font-medium">{translated.text(0)}</p>
                            {item.hs_code_uganda && (
                              <Badge variant="secondary" title="Uganda HS code">
                                HS {item.hs_code_uganda}
                              </Badge>
                            )}
                            {item.hs_code_needs_review && (
                              <Badge
                                variant="accent-soft"
                                title="Not in the tariff schedule. Duty and documents leave it out until someone confirms it."
                              >
                                Needs review
                              </Badge>
                            )}
                            {item.is_special_item && <Badge variant="outline">Special</Badge>}
                            {packageByItem.get(item.id) && (
                              <Link
                                to={`/consignments/${consignment.id}/tracking#package-${packageByItem.get(item.id)!.id}`}
                                className="rounded-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                                title={`Package ${packageByItem.get(item.id)!.tracking_number}`}
                              >
                                <Badge
                                  variant="outline"
                                  className={
                                    GATHERING_STATE[packageByItem.get(item.id)!.state].tone
                                  }
                                >
                                  {GATHERING_STATE[packageByItem.get(item.id)!.state].short}
                                </Badge>
                              </Link>
                            )}
                          </div>
                          <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
                            {translated.text(1)}
                          </p>
                          <TranslateToggle block={translated} label={item.name} className="mt-1" />
                        </div>

                        <dl className="col-start-2 grid grid-cols-3 gap-x-4 gap-y-2 lg:col-start-auto lg:min-w-[21rem]">
                          <div className="min-w-0">
                            <dt className="text-xs text-muted-foreground">Quantity</dt>
                            <dd className="truncate text-sm font-medium">{item.quantity}</dd>
                          </div>
                          <div className="min-w-0 text-right">
                            <dt className="text-xs text-muted-foreground">Unit price</dt>
                            <dd className="truncate text-sm font-medium">
                              {formatCurrency(item.unit_price)}
                            </dd>
                          </div>
                          <div className="min-w-0 text-right">
                            <dt className="text-xs text-muted-foreground">Total</dt>
                            <dd className="truncate text-sm font-semibold">
                              {formatCurrency(unitTotal)}
                            </dd>
                          </div>
                        </dl>

                        {isAdmin && (
                          <div className="col-start-2 flex justify-end gap-2 lg:col-start-auto">
                            {item.hs_code_needs_review && item.hs_code_uganda && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => confirmCode(item)}
                                disabled={
                                  Boolean(savingItemId) || classifying || Boolean(editingItemId)
                                }
                                aria-label={`Confirm HS code ${item.hs_code_uganda} for ${item.name}`}
                              >
                                {savingItemId === item.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="h-4 w-4" />
                                )}
                                Confirm
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => startEditing(item)}
                              disabled={
                                Boolean(savingItemId) || classifying || Boolean(editingItemId)
                              }
                              aria-label={`Edit ${item.name}`}
                            >
                              <Pencil className="h-4 w-4" />
                              Edit
                            </Button>
                          </div>
                        )}
                      </div>

                      {isExpanded && !isEditing && (
                        <div
                          id={`consignment-item-details-${item.id}`}
                          className="border-t bg-muted/15 p-4"
                        >
                          <dl className="grid min-w-0 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                            <div className="min-w-0 sm:col-span-2 lg:col-span-3">
                              <dt className="text-xs font-medium text-muted-foreground">
                                Description
                              </dt>
                              <dd className="mt-1 whitespace-pre-wrap text-sm">
                                {translated.text(1)}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="text-xs font-medium text-muted-foreground">
                                Uganda HS code
                              </dt>
                              <dd className="mt-1 break-words text-sm">
                                {item.hs_code_uganda || item.hs_code || 'Pending'}
                                {item.hs_code_needs_review && item.hs_code_uganda
                                  ? ' · needs review, not in the tariff schedule'
                                  : ''}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="text-xs font-medium text-muted-foreground">
                                China HS code
                              </dt>
                              <dd className="mt-1 break-words text-sm">
                                {item.hs_code_china || 'Not set'}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="text-xs font-medium text-muted-foreground">
                                Tracking
                              </dt>
                              <dd className="mt-1 break-words text-sm">
                                {item.tracking_number || 'Not set'}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="text-xs font-medium text-muted-foreground">
                                Product use
                              </dt>
                              <dd className="mt-1 break-words text-sm">
                                {item.product_use || 'Not set'}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="text-xs font-medium text-muted-foreground">
                                Material
                              </dt>
                              <dd className="mt-1 break-words text-sm">
                                {item.material || 'Not set'}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="text-xs font-medium text-muted-foreground">
                                Special item
                              </dt>
                              <dd className="mt-1 text-sm">
                                {item.is_special_item ? 'Yes' : 'No'}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      )}

                      {isEditing && draftItem && (
                        <div
                          id={`consignment-item-details-${item.id}`}
                          className="border-t bg-muted/15 p-4"
                          data-testid={`consignment-item-editor-${item.id}`}
                        >
                          <div className="grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <div className="sm:col-span-2 lg:col-span-3">
                              <Label htmlFor={`item-description-${item.id}`}>Description</Label>
                              <Textarea
                                id={`item-description-${item.id}`}
                                className="mt-1.5"
                                aria-label={`Description for ${item.name}`}
                                value={draftItem.description || draftItem.name}
                                onChange={(event) =>
                                  patchDraft({ description: event.target.value })
                                }
                                rows={3}
                              />
                            </div>
                            <div>
                              <Label htmlFor={`item-quantity-${item.id}`}>Quantity</Label>
                              <Input
                                id={`item-quantity-${item.id}`}
                                className="mt-1.5"
                                aria-label={`Quantity for ${item.name}`}
                                type="number"
                                min="1"
                                value={draftItem.quantity}
                                onChange={(event) =>
                                  patchDraft({ quantity: Number(event.target.value) || 1 })
                                }
                              />
                            </div>
                            <div>
                              <Label htmlFor={`item-unit-price-${item.id}`}>Unit price</Label>
                              <Input
                                id={`item-unit-price-${item.id}`}
                                className="mt-1.5"
                                aria-label={`Unit price for ${item.name}`}
                                type="number"
                                min="0"
                                step="0.01"
                                value={draftItem.unit_price}
                                onChange={(event) =>
                                  patchDraft({ unit_price: Number(event.target.value) || 0 })
                                }
                              />
                            </div>
                            <div>
                              <Label>Total</Label>
                              <div className="mt-1.5 flex h-9 items-center rounded-md border bg-background px-3 text-sm font-medium">
                                {formatCurrency(draftItem.quantity * draftItem.unit_price)}
                              </div>
                            </div>
                            <div>
                              <Label htmlFor={`item-hs-uganda-${item.id}`}>Uganda HS code</Label>
                              <Input
                                id={`item-hs-uganda-${item.id}`}
                                className="mt-1.5"
                                aria-label={`Uganda HS code for ${item.name}`}
                                value={draftItem.hs_code_uganda || ''}
                                onChange={(event) =>
                                  patchDraft({ hs_code_uganda: event.target.value })
                                }
                              />
                              {item.hs_code_needs_review && draftItem.hs_code_uganda && (
                                <p className="mt-1 text-xs text-muted-foreground">
                                  Not in the tariff schedule. Saving confirms this code.
                                </p>
                              )}
                            </div>
                            <div>
                              <Label htmlFor={`item-hs-china-${item.id}`}>China HS code</Label>
                              <Input
                                id={`item-hs-china-${item.id}`}
                                className="mt-1.5"
                                aria-label={`China HS code for ${item.name}`}
                                value={draftItem.hs_code_china || ''}
                                onChange={(event) =>
                                  patchDraft({ hs_code_china: event.target.value })
                                }
                              />
                            </div>
                            <div>
                              <Label htmlFor={`item-tracking-${item.id}`}>Tracking</Label>
                              <Input
                                id={`item-tracking-${item.id}`}
                                className="mt-1.5"
                                aria-label={`Tracking number for ${item.name}`}
                                value={draftItem.tracking_number || ''}
                                onChange={(event) =>
                                  patchDraft({ tracking_number: event.target.value })
                                }
                              />
                            </div>
                            <div>
                              <Label htmlFor={`item-use-${item.id}`}>Product use</Label>
                              <Input
                                id={`item-use-${item.id}`}
                                className="mt-1.5"
                                aria-label={`Product use for ${item.name}`}
                                value={draftItem.product_use || ''}
                                onChange={(event) =>
                                  patchDraft({ product_use: event.target.value })
                                }
                              />
                            </div>
                            <div>
                              <Label htmlFor={`item-material-${item.id}`}>Material</Label>
                              <Input
                                id={`item-material-${item.id}`}
                                className="mt-1.5"
                                aria-label={`Material for ${item.name}`}
                                value={draftItem.material || ''}
                                onChange={(event) => patchDraft({ material: event.target.value })}
                              />
                            </div>
                            <label className="flex min-h-9 items-center gap-2 self-end rounded-md border bg-background px-3 py-2 text-sm">
                              <Checkbox
                                checked={Boolean(draftItem.is_special_item)}
                                onCheckedChange={(checked) =>
                                  patchDraft({ is_special_item: checked === true })
                                }
                              />
                              Special item
                            </label>
                          </div>
                          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={cancelEditing}
                              disabled={savingItemId === item.id}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              onClick={() => saveItem(draftItem)}
                              disabled={savingItemId === item.id}
                            >
                              {savingItemId === item.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Save className="h-4 w-4" />
                              )}
                              Save
                            </Button>
                          </div>
                        </div>
                      )}
                    </article>
                  )}
                </Translated>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TaxOperationsPanel({
  consignment,
  isAdmin,
  onChanged,
}: {
  consignment: ConsignmentDetail
  isAdmin: boolean
  onChanged: () => void
}) {
  const [recalculating, setRecalculating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const taxEstimate = consignment.tax_estimate ?? null
  // The API leaves these out of the estimate, as it does items with no code.
  const awaitingReview = consignment.items
    .filter((item) => item.hs_code_needs_review && item.hs_code_uganda)
    .map((item) => item.name)

  async function handleRecalculate() {
    setRecalculating(true)
    setError(null)
    setMessage(null)
    try {
      const result = await apiClient.recalculateConsignmentTaxes(consignment.id)
      setMessage(
        `Recalculated ${result.items.length} item tax lines. Total tax: ${formatCurrency(result.totals.total_tax, 'UGX')}`,
      )
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to recalculate taxes')
    } finally {
      setRecalculating(false)
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>Item Tax Lines</CardTitle>
              <CardDescription>
                Duty, VAT, withholding, infrastructure levy, and excise values per item.
              </CardDescription>
            </div>
            {isAdmin && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRecalculate}
                disabled={recalculating}
              >
                {recalculating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Calculator className="h-4 w-4" />
                )}
                Estimate Tax
              </Button>
            )}
          </div>
        </CardHeader>
        {/* On a phone the cards below stand in for the table, container and all. */}
        <CardContent className="space-y-4 max-md:[&>[data-slot=table-container]]:hidden">
          {awaitingReview.length > 0 && (
            <Alert variant="destructive" data-testid="tax-awaiting-review">
              <AlertDescription>{taxReviewWarning(awaitingReview)}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {message && (
            <Alert>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Duty</TableHead>
                <TableHead className="text-right">VAT</TableHead>
                <TableHead className="text-right">WHT</TableHead>
                <TableHead className="text-right">Infra</TableHead>
                <TableHead className="text-right">Excise</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {consignment.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="font-medium">{item.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {item.hs_code_needs_review && item.hs_code_uganda
                        ? `${item.hs_code_uganda} · needs review, left out`
                        : item.hs_code_uganda || item.hs_code || 'Missing HS code'}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.import_duty, 'UGX')}
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(item.vat, 'UGX')}</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.withholding_tax, 'UGX')}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.infra_levy, 'UGX')}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.excise_duty, 'UGX')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {/* On a phone, each item's taxes as a card rather than six columns off the screen. */}
          <ul aria-label="Item tax lines" className="divide-y rounded-xl border md:hidden">
            {consignment.items.map((item) => (
              <li key={item.id} className="space-y-2 p-3">
                <div>
                  <p className="text-sm font-medium wrap-anywhere">{item.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {item.hs_code_needs_review && item.hs_code_uganda
                      ? `${item.hs_code_uganda} · needs review, left out`
                      : item.hs_code_uganda || item.hs_code || 'Missing HS code'}
                  </p>
                </div>
                <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-xs">
                  {(
                    [
                      ['Duty', item.import_duty],
                      ['VAT', item.vat],
                      ['WHT', item.withholding_tax],
                      ['Infra', item.infra_levy],
                      ['Excise', item.excise_duty],
                    ] as const
                  ).map(([label, amount]) => (
                    <div key={label} className="min-w-0">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="font-medium wrap-anywhere">{formatCurrency(amount, 'UGX')}</dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ReceiptText className="h-4 w-4" />
            Estimate Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {taxEstimate ? (
            <>
              <div>
                <div className="text-sm text-muted-foreground">Total Tax</div>
                <div className="text-2xl font-semibold">
                  {formatCurrency(taxEstimate.total_tax, taxEstimate.target_currency || 'UGX')}
                </div>
              </div>
              <Separator />
              <TaxAmountRow
                label="CIF Value"
                value={taxEstimate.cif_value}
                currency={taxEstimate.currency}
              />
              <TaxAmountRow
                label="Import Duty"
                value={taxEstimate.import_duty}
                currency={taxEstimate.target_currency}
              />
              <TaxAmountRow
                label="VAT"
                value={taxEstimate.vat}
                currency={taxEstimate.target_currency}
              />
              <TaxAmountRow
                label="Withholding"
                value={taxEstimate.withholding_tax}
                currency={taxEstimate.target_currency}
              />
              <TaxAmountRow
                label="Infrastructure Levy"
                value={taxEstimate.infra_levy}
                currency={taxEstimate.target_currency}
              />
              <TaxAmountRow
                label="Excise Duty"
                value={taxEstimate.excise_duty}
                currency={taxEstimate.target_currency}
              />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              No tax estimate has been calculated for this consignment.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function documentHref(document: ConsignmentDocument) {
  return apiClient.getDocumentDownloadUrl(document.id)
}

const goxBusinessTypeOptions: Array<{
  value: GoxBookingNotePayload['businessType']
  label: string
}> = [
  { value: 'FCL_FCL', label: 'FCL / FCL' },
  { value: 'LCL_LCL', label: 'LCL / LCL' },
  { value: 'FCL_LCL', label: 'FCL / LCL' },
  { value: 'LCL_FCL', label: 'LCL / FCL' },
]

const goxDepartureServices = [
  'Booking',
  'Trailer',
  'CFS Loading',
  'Customs Declaration',
  'Insurance',
]
const goxDestinationServices = [
  'Delivery Order',
  'Storage',
  'Dispatch',
  'Customs Clearance',
  'Insurance',
]
const goxCarrierOptions = ['GOX', 'PCIU', 'MAERSK', 'MSC', 'CMA CGM', 'COSCO', 'OOCL']
const goxPortOptions = [
  'SHEKOU PT',
  'SHENZHEN',
  'NINGBO',
  'SHANGHAI',
  'GUANGZHOU',
  'MOMBASA',
  'ENTEBBE',
  'KAMPALA',
]
const goxContainerOptions = ['20GP', '40GP', '40HQ', '45HQ', 'LCL']
const goxCargoTypeOptions = [
  'General Cargo',
  'Consolidated Cargo',
  'Dangerous Goods',
  'Reefer Cargo',
  'Oversized Cargo',
]
const goxPackageTypeOptions = ['PCS', 'CTNS', 'PALLETS', 'WOODEN CASES', 'BAGS', 'ROLLS']
const goxBlTypeOptions = ['MB/L', 'HB/L', 'SWB']
const goxIncotermOptions = ['EXW', 'FOB', 'CIF', 'CFR', 'DAP', 'DDP']
const goxReleaseTypeOptions: Array<{ value: GoxBookingNotePayload['releaseType']; label: string }> =
  [
    { value: 'ORIGINAL', label: 'Original' },
    { value: 'TELEX_RELEASE', label: 'Telex release' },
    { value: 'SEAWAY_BILL', label: 'Seaway bill' },
  ]

function selectOptionsWithValue(options: string[], value: string) {
  return value && !options.includes(value) ? [value, ...options] : options
}

function firstText(...values: Array<string | null | undefined>) {
  return values.find((value) => value?.trim())?.trim() || ''
}

function upperText(value: string | null | undefined, fallback: string) {
  return firstText(value, fallback).toUpperCase()
}

function formattedAddress(address: Record<string, string | null> | null | undefined) {
  if (!address) return ''
  return [
    address.attention,
    address.address,
    address.street2,
    address.city,
    address.state,
    address.zip,
    address.country,
  ]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(', ')
}

function dateInputValue(value: string | null | undefined) {
  if (!value) return ''
  return value.slice(0, 10)
}

function todayInputValue() {
  return new Date().toISOString().slice(0, 10)
}

function destinationCity(value: string | null | undefined) {
  const parts = String(value || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  return parts[parts.length - 2] || parts[0] || 'KAMPALA'
}

function primarySeaShipment(consignment: ConsignmentDetail): ConsignmentSeaShipment | null {
  return (
    consignment.sea_shipments.find(
      (shipment) =>
        shipment.reference?.trim() ||
        shipment.containers.some((container) => container.container_number?.trim()),
    ) ?? null
  )
}

function containerTypeFromShipment(shipment: ConsignmentSeaShipment | null, fallback: string) {
  const firstContainer = shipment?.containers[0]
  const typed = [firstContainer?.container_size, firstContainer?.container_type]
    .filter(Boolean)
    .join('')
  return typed || fallback
}

function cargoRowsFromItems(items: ConsignmentItem[]): GoxBookingCargoRow[] {
  if (items.length === 0) {
    return [
      {
        factoryName: '',
        packingListRef: '',
        marksAndNumbers: 'N/M',
        packages: '1 PCS',
        goodsDescription: 'General Cargo',
        grossWeightKg: '',
        measurementCbm: '',
      },
    ]
  }

  return items.map((item) => ({
    factoryName: '',
    packingListRef: item.tracking_number || '',
    marksAndNumbers: 'N/M',
    packages: `${item.quantity || 1} PCS`,
    goodsDescription: item.simple_name || item.description || item.name || 'Goods',
    grossWeightKg: '',
    measurementCbm: '',
  }))
}

function syncNotifier(payload: GoxBookingNotePayload): GoxBookingNotePayload {
  if (!payload.notifySameAsConsignee) return payload
  return {
    ...payload,
    notifierName: payload.consigneeName,
    notifierAddress: payload.consigneeAddress,
    notifierContactPerson: payload.consigneeContactPerson,
    notifierEmail: payload.consigneeEmail,
    notifierPhone: payload.consigneePhone,
  }
}

function buildGoxBookingNotePayload(consignment: ConsignmentDetail): GoxBookingNotePayload {
  const customer = consignment.customer
  const customerName = firstText(customer?.company_name, customer?.name, 'Customer')
  const contactPerson = firstText(customer?.contact_person, customer?.name)
  const shipment = primarySeaShipment(consignment)
  const seaContainerCount = shipment?.containers.length || 0
  const destination = firstText(
    consignment.destination_address,
    consignment.destination_port,
    formattedAddress(customer?.billing_address),
    'Kampala, Uganda',
  )
  const shippingMethod = String(consignment.shipping_method || consignment.mode || '').toUpperCase()
  const shipmentReference = firstText(
    shipment?.reference,
    consignment.international_waybill,
    consignment.international_tracking_number,
    consignment.forwarder_internal_reference,
    consignment.tracking_number,
    consignment.id.slice(0, 8).toUpperCase(),
  )
  const carrier = firstText(shipment?.carrier_scac, consignment.preferred_carrier, 'GOX')
  const containerFallback = shippingMethod === 'SEA' ? '20GP' : 'LCL'

  return syncNotifier({
    shipmentReference,
    associatedSales: '',
    businessType: shippingMethod === 'SEA' && seaContainerCount > 0 ? 'FCL_FCL' : 'LCL_LCL',
    carrier,
    departureServices: ['Booking', 'Customs Declaration'],
    destinationServices: ['Delivery Order', 'Customs Clearance'],
    placeOfReceipt: upperText(consignment.origin_country, 'SHEKOU PT'),
    portOfLoading: shippingMethod === 'SEA' ? 'SHEKOU PT' : 'SHENZHEN',
    portOfDischarge: shippingMethod === 'SEA' ? 'MOMBASA' : 'ENTEBBE',
    placeOfDelivery: upperText(destinationCity(destination), 'KAMPALA'),
    etd: '',
    estimatedArrival: dateInputValue(consignment.expected_arrival),
    cargoReadyDate: todayInputValue(),
    shipperContact: '',
    containerType: containerTypeFromShipment(shipment, containerFallback),
    containerQuantity: String(seaContainerCount || 1),
    cargoType: 'General Cargo',
    packageType: 'PCS',
    blType: 'MB/L',
    incoterms: 'EXW',
    shipperName: '',
    shipperAddress: '',
    consigneeName: customerName,
    consigneeAddress: destination,
    consigneeContactPerson: contactPerson,
    consigneeEmail: customer?.email || '',
    consigneePhone: customer?.phone || '',
    consigneeTin: customer?.tin_number || '',
    notifierName: customerName,
    notifierAddress: destination,
    notifierContactPerson: contactPerson,
    notifierEmail: customer?.email || '',
    notifierPhone: customer?.phone || '',
    notifySameAsConsignee: true,
    freeTime: 'On-site + Off-site',
    freeTimeDays: '',
    poNumber: consignment.forwarder_internal_reference || '',
    releaseType: 'TELEX_RELEASE',
    toOrder: false,
    telexRelease: true,
    freightPrepaid: true,
    freightCollect: false,
    factoryPickup: false,
    customsDeclaration: true,
    solidWoodPacking: false,
    shipperArrangedFumigation: false,
    forwarderArrangedFumigation: false,
    remarks: '',
    cargoRows: cargoRowsFromItems(consignment.items),
  })
}

function GoxField({
  label,
  children,
  className = '',
}: {
  label: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  )
}

function GoxSelectField({
  label,
  value,
  options,
  onValueChange,
  className = '',
}: {
  label: string
  value: string
  options: Array<string | { value: string; label: string }>
  onValueChange: (value: string) => void
  className?: string
}) {
  return (
    <GoxField label={label} className={className}>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => {
            const value = typeof option === 'string' ? option : option.value
            const label = typeof option === 'string' ? option : option.label
            return (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </GoxField>
  )
}

function GoxTextField({
  label,
  value,
  onChange,
  type = 'text',
  className = '',
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  className?: string
}) {
  return (
    <GoxField label={label} className={className}>
      <Input type={type} value={value} onChange={(event) => onChange(event.target.value)} />
    </GoxField>
  )
}

function GoxCheckbox({
  label,
  checked,
  onCheckedChange,
}: {
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
      <Checkbox checked={checked} onCheckedChange={(value) => onCheckedChange(value === true)} />
      <span>{label}</span>
    </label>
  )
}

function ServiceToggleGroup({
  options,
  values,
  onChange,
}: {
  options: string[]
  values: string[]
  onChange: (values: string[]) => void
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selected = values.includes(option)
        return (
          <Button
            key={option}
            type="button"
            size="sm"
            variant={selected ? 'default' : 'outline'}
            onClick={() =>
              onChange(selected ? values.filter((value) => value !== option) : [...values, option])
            }
          >
            {option}
          </Button>
        )
      })}
    </div>
  )
}

function GoxBookingNoteDialog({
  consignment,
  busy,
  onGenerate,
}: {
  consignment: ConsignmentDetail
  busy: boolean
  onGenerate: (payload: GoxBookingNotePayload) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [payload, setPayload] = useState<GoxBookingNotePayload>(() =>
    buildGoxBookingNotePayload(consignment),
  )

  useEffect(() => {
    if (open) setPayload(buildGoxBookingNotePayload(consignment))
  }, [open, consignment])

  function updatePayload(patch: Partial<GoxBookingNotePayload>) {
    setPayload((current) => syncNotifier({ ...current, ...patch }))
  }

  function updateCargoRow(index: number, patch: Partial<GoxBookingCargoRow>) {
    setPayload((current) => ({
      ...current,
      cargoRows: current.cargoRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    }))
  }

  function addCargoRow() {
    updatePayload({
      cargoRows: [
        ...payload.cargoRows,
        {
          factoryName: '',
          packingListRef: '',
          marksAndNumbers: 'N/M',
          packages: '1 PCS',
          goodsDescription: '',
          grossWeightKg: '',
          measurementCbm: '',
        },
      ],
    })
  }

  function removeCargoRow(index: number) {
    if (payload.cargoRows.length <= 1) return
    updatePayload({ cargoRows: payload.cargoRows.filter((_, rowIndex) => rowIndex !== index) })
  }

  async function handleGenerate() {
    const cargoRows = payload.cargoRows.map((row) => ({
      ...row,
      goodsDescription: row.goodsDescription.trim() || 'General Cargo',
      packages: row.packages.trim() || `1 ${payload.packageType}`,
      marksAndNumbers: row.marksAndNumbers.trim() || 'N/M',
    }))
    await onGenerate(syncNotifier({ ...payload, cargoRows }))
    setOpen(false)
  }

  const freightValue = payload.freightCollect ? 'COLLECT' : 'PREPAID'

  return (
    <>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        disabled={busy}
        className="w-full sm:w-auto"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />}
        Create Booking Note
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[1100px]">
          <DialogHeader>
            <DialogTitle>Booking Note</DialogTitle>
            <DialogDescription>
              Review shipment, party, and cargo details before generating the booking note PDF.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="grid gap-3 md:grid-cols-4">
              <GoxTextField
                label="Sea shipment"
                value={payload.shipmentReference}
                onChange={(value) => updatePayload({ shipmentReference: value })}
              />
              <GoxSelectField
                label="Business type"
                value={payload.businessType}
                options={goxBusinessTypeOptions}
                onValueChange={(value) =>
                  updatePayload({ businessType: value as GoxBookingNotePayload['businessType'] })
                }
              />
              <GoxSelectField
                label="Carrier"
                value={payload.carrier}
                options={selectOptionsWithValue(goxCarrierOptions, payload.carrier)}
                onValueChange={(value) => updatePayload({ carrier: value })}
              />
              <GoxTextField
                label="Associated sales"
                value={payload.associatedSales}
                onChange={(value) => updatePayload({ associatedSales: value })}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2 rounded-md border p-3">
                <Label className="text-sm font-medium">Departure port services</Label>
                <ServiceToggleGroup
                  options={goxDepartureServices}
                  values={payload.departureServices}
                  onChange={(values) => updatePayload({ departureServices: values })}
                />
              </div>
              <div className="space-y-2 rounded-md border p-3">
                <Label className="text-sm font-medium">Destination port services</Label>
                <ServiceToggleGroup
                  options={goxDestinationServices}
                  values={payload.destinationServices}
                  onChange={(values) => updatePayload({ destinationServices: values })}
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <GoxTextField
                label="Place of receipt"
                value={payload.placeOfReceipt}
                onChange={(value) => updatePayload({ placeOfReceipt: value })}
              />
              <GoxSelectField
                label="POL"
                value={payload.portOfLoading}
                options={selectOptionsWithValue(goxPortOptions, payload.portOfLoading)}
                onValueChange={(value) => updatePayload({ portOfLoading: value })}
              />
              <GoxSelectField
                label="POD"
                value={payload.portOfDischarge}
                options={selectOptionsWithValue(goxPortOptions, payload.portOfDischarge)}
                onValueChange={(value) => updatePayload({ portOfDischarge: value })}
              />
              <GoxTextField
                label="Place of delivery"
                value={payload.placeOfDelivery}
                onChange={(value) => updatePayload({ placeOfDelivery: value })}
              />
              <GoxTextField
                label="ETD"
                type="date"
                value={payload.etd}
                onChange={(value) => updatePayload({ etd: value })}
              />
              <GoxTextField
                label="Estimated arrival"
                type="date"
                value={payload.estimatedArrival}
                onChange={(value) => updatePayload({ estimatedArrival: value })}
              />
              <GoxTextField
                label="Cargo ready date"
                type="date"
                value={payload.cargoReadyDate}
                onChange={(value) => updatePayload({ cargoReadyDate: value })}
              />
              <GoxTextField
                label="Shipper contact"
                value={payload.shipperContact}
                onChange={(value) => updatePayload({ shipperContact: value })}
              />
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <GoxSelectField
                label="Container type"
                value={payload.containerType}
                options={selectOptionsWithValue(goxContainerOptions, payload.containerType)}
                onValueChange={(value) => updatePayload({ containerType: value })}
              />
              <GoxTextField
                label="Container quantity"
                value={payload.containerQuantity}
                onChange={(value) => updatePayload({ containerQuantity: value })}
              />
              <GoxSelectField
                label="Cargo type"
                value={payload.cargoType}
                options={selectOptionsWithValue(goxCargoTypeOptions, payload.cargoType)}
                onValueChange={(value) => updatePayload({ cargoType: value })}
              />
              <GoxSelectField
                label="Package type"
                value={payload.packageType}
                options={selectOptionsWithValue(goxPackageTypeOptions, payload.packageType)}
                onValueChange={(value) => updatePayload({ packageType: value })}
              />
              <GoxSelectField
                label="B/L type"
                value={payload.blType}
                options={selectOptionsWithValue(goxBlTypeOptions, payload.blType)}
                onValueChange={(value) => updatePayload({ blType: value })}
              />
              <GoxSelectField
                label="Incoterms"
                value={payload.incoterms}
                options={selectOptionsWithValue(goxIncotermOptions, payload.incoterms)}
                onValueChange={(value) => updatePayload({ incoterms: value })}
              />
              <GoxTextField
                label="Free time"
                value={payload.freeTime}
                onChange={(value) => updatePayload({ freeTime: value })}
              />
              <GoxTextField
                label="Free time days"
                value={payload.freeTimeDays}
                onChange={(value) => updatePayload({ freeTimeDays: value })}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              <div className="space-y-3 rounded-md border p-3">
                <Label className="text-sm font-medium">Shipper</Label>
                <GoxTextField
                  label="Factory / supplier"
                  value={payload.shipperName}
                  onChange={(value) => updatePayload({ shipperName: value })}
                />
                <GoxField label="Factory address">
                  <Textarea
                    value={payload.shipperAddress}
                    onChange={(event) => updatePayload({ shipperAddress: event.target.value })}
                    rows={4}
                  />
                </GoxField>
              </div>

              <div className="space-y-3 rounded-md border p-3">
                <Label className="text-sm font-medium">Consignee</Label>
                <GoxTextField
                  label="Name"
                  value={payload.consigneeName}
                  onChange={(value) => updatePayload({ consigneeName: value })}
                />
                <GoxField label="Address">
                  <Textarea
                    value={payload.consigneeAddress}
                    onChange={(event) => updatePayload({ consigneeAddress: event.target.value })}
                    rows={3}
                  />
                </GoxField>
                <GoxTextField
                  label="Contact"
                  value={payload.consigneeContactPerson}
                  onChange={(value) => updatePayload({ consigneeContactPerson: value })}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <GoxTextField
                    label="Email"
                    value={payload.consigneeEmail}
                    onChange={(value) => updatePayload({ consigneeEmail: value })}
                  />
                  <GoxTextField
                    label="Phone"
                    value={payload.consigneePhone}
                    onChange={(value) => updatePayload({ consigneePhone: value })}
                  />
                </div>
              </div>

              <div className="space-y-3 rounded-md border p-3">
                <div className="flex items-center justify-between gap-3">
                  <Label className="text-sm font-medium">Notifier</Label>
                  <GoxCheckbox
                    label="Same as consignee"
                    checked={payload.notifySameAsConsignee}
                    onCheckedChange={(checked) => updatePayload({ notifySameAsConsignee: checked })}
                  />
                </div>
                <GoxTextField
                  label="Name"
                  value={payload.notifierName}
                  onChange={(value) => updatePayload({ notifierName: value })}
                />
                <GoxField label="Address">
                  <Textarea
                    value={payload.notifierAddress}
                    onChange={(event) => updatePayload({ notifierAddress: event.target.value })}
                    rows={3}
                    disabled={payload.notifySameAsConsignee}
                  />
                </GoxField>
                <GoxTextField
                  label="Contact"
                  value={payload.notifierContactPerson}
                  onChange={(value) => updatePayload({ notifierContactPerson: value })}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <GoxTextField
                    label="Email"
                    value={payload.notifierEmail}
                    onChange={(value) => updatePayload({ notifierEmail: value })}
                  />
                  <GoxTextField
                    label="Phone"
                    value={payload.notifierPhone}
                    onChange={(value) => updatePayload({ notifierPhone: value })}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm font-medium">Cargo rows</Label>
                <Button type="button" size="sm" variant="outline" onClick={addCargoRow}>
                  <Plus className="h-4 w-4" />
                  Add Row
                </Button>
              </div>
              <div className="space-y-3">
                {payload.cargoRows.map((row, index) => (
                  <div
                    key={index}
                    className="grid gap-3 rounded-md border p-3 lg:grid-cols-[1fr_1fr_1fr_1fr_1.5fr_0.8fr_0.8fr_auto]"
                  >
                    <GoxTextField
                      label="Factory"
                      value={row.factoryName}
                      onChange={(value) => updateCargoRow(index, { factoryName: value })}
                    />
                    <GoxTextField
                      label="Packing list"
                      value={row.packingListRef}
                      onChange={(value) => updateCargoRow(index, { packingListRef: value })}
                    />
                    <GoxTextField
                      label="Marks"
                      value={row.marksAndNumbers}
                      onChange={(value) => updateCargoRow(index, { marksAndNumbers: value })}
                    />
                    <GoxTextField
                      label="Packages"
                      value={row.packages}
                      onChange={(value) => updateCargoRow(index, { packages: value })}
                    />
                    <GoxTextField
                      label="Description"
                      value={row.goodsDescription}
                      onChange={(value) => updateCargoRow(index, { goodsDescription: value })}
                    />
                    <GoxTextField
                      label="Gross KG"
                      value={row.grossWeightKg}
                      onChange={(value) => updateCargoRow(index, { grossWeightKg: value })}
                    />
                    <GoxTextField
                      label="CBM"
                      value={row.measurementCbm}
                      onChange={(value) => updateCargoRow(index, { measurementCbm: value })}
                    />
                    <div className="flex items-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeCargoRow(index)}
                        disabled={payload.cargoRows.length <= 1}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Remove row</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <GoxTextField
                label="PO No."
                value={payload.poNumber}
                onChange={(value) => updatePayload({ poNumber: value })}
              />
              <GoxSelectField
                label="Release type"
                value={payload.releaseType}
                options={goxReleaseTypeOptions}
                onValueChange={(value) =>
                  updatePayload({
                    releaseType: value as GoxBookingNotePayload['releaseType'],
                    telexRelease: value === 'TELEX_RELEASE',
                  })
                }
              />
              <GoxSelectField
                label="Freight terms"
                value={freightValue}
                options={[
                  { value: 'PREPAID', label: 'Freight prepaid' },
                  { value: 'COLLECT', label: 'Freight collect' },
                ]}
                onValueChange={(value) =>
                  updatePayload({
                    freightPrepaid: value === 'PREPAID',
                    freightCollect: value === 'COLLECT',
                  })
                }
              />
              <GoxCheckbox
                label="To order"
                checked={payload.toOrder}
                onCheckedChange={(checked) => updatePayload({ toOrder: checked })}
              />
              <GoxCheckbox
                label="Factory pickup"
                checked={payload.factoryPickup}
                onCheckedChange={(checked) => updatePayload({ factoryPickup: checked })}
              />
              <GoxCheckbox
                label="Customs declaration"
                checked={payload.customsDeclaration}
                onCheckedChange={(checked) => updatePayload({ customsDeclaration: checked })}
              />
              <GoxCheckbox
                label="Solid wood packing"
                checked={payload.solidWoodPacking}
                onCheckedChange={(checked) => updatePayload({ solidWoodPacking: checked })}
              />
              <GoxCheckbox
                label="Shipper fumigation"
                checked={payload.shipperArrangedFumigation}
                onCheckedChange={(checked) => updatePayload({ shipperArrangedFumigation: checked })}
              />
              <GoxCheckbox
                label="Forwarder fumigation"
                checked={payload.forwarderArrangedFumigation}
                onCheckedChange={(checked) =>
                  updatePayload({ forwarderArrangedFumigation: checked })
                }
              />
            </div>

            <GoxField label="Remarks">
              <Textarea
                value={payload.remarks}
                onChange={(event) => updatePayload({ remarks: event.target.value })}
                rows={3}
              />
            </GoxField>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" onClick={handleGenerate} disabled={busy}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FilePlus2 className="h-4 w-4" />
              )}
              Generate PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function documentDefinitionLabel(
  definitions: ConsignmentDocumentDefinition[],
  type: ConsignmentDocumentType,
) {
  return definitions.find((definition) => definition.type === type)?.label ?? formatLabel(type)
}

function documentMetadataValue(document: ConsignmentDocument, key: string) {
  const value = document.metadata?.[key]
  return typeof value === 'string' && value.trim() ? value : null
}

function isImageDocument(document: ConsignmentDocument) {
  const contentType = documentMetadataValue(document, 'content_type')
  if (contentType?.startsWith('image/')) return true
  return /\.(avif|gif|jpe?g|png|webp)$/i.test(document.file_name ?? '')
}

function GenerateDocumentDialog({
  open,
  onOpenChange,
  definitions,
  initialType,
  onGenerate,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  definitions: ConsignmentDocumentDefinition[]
  initialType?: ConsignmentDocumentType | null
  onGenerate: (type: ConsignmentDocumentType) => Promise<void>
}) {
  const options = definitions.filter(
    (definition) =>
      definition.type !== 'GOX_BOOKING_NOTE' && definition.capability !== 'UPLOAD_ONLY',
  )
  const [type, setType] = useState<ConsignmentDocumentType>(
    initialType ?? options[0]?.type ?? 'COMMERCIAL_INVOICE',
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setType(initialType ?? options[0]?.type ?? 'COMMERCIAL_INVOICE')
    setError(null)
  }, [initialType, open])

  const selected = definitions.find((definition) => definition.type === type)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await onGenerate(type)
      onOpenChange(false)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to generate document')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate Document</DialogTitle>
          <DialogDescription>
            Create an Xtellar-issued document from the current consignment data.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-5" onSubmit={submit}>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="generated-document-type">Document type</Label>
            <Select
              value={type}
              onValueChange={(value) => setType(value as ConsignmentDocumentType)}
            >
              <SelectTrigger id="generated-document-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="start">
                {options.map((definition) => (
                  <SelectItem key={definition.type} value={definition.type}>
                    {definition.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected && <p className="text-sm text-muted-foreground">{selected.description}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || options.length === 0}>
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FilePlus2 className="h-4 w-4" />
              )}
              Generate
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function UploadDocumentDialog({
  open,
  onOpenChange,
  definitions,
  initialType,
  replacement,
  onUpload,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  definitions: ConsignmentDocumentDefinition[]
  initialType?: ConsignmentDocumentType | null
  replacement?: ConsignmentDocument | null
  onUpload: (input: {
    type: ConsignmentDocumentType
    file: File
    replacementId: string | null
    description: string | null
    issuer: string | null
    receivedAt: string | null
    expiresAt: string | null
  }) => Promise<void>
}) {
  const options = definitions.filter((definition) => definition.capability !== 'GENERATED')
  const [type, setType] = useState<ConsignmentDocumentType>(
    initialType ?? replacement?.type ?? options[0]?.type ?? 'ORIGINAL_INVOICE',
  )
  const [file, setFile] = useState<File | null>(null)
  const [description, setDescription] = useState('')
  const [issuer, setIssuer] = useState('')
  const [receivedAt, setReceivedAt] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setType(initialType ?? replacement?.type ?? options[0]?.type ?? 'ORIGINAL_INVOICE')
    setFile(null)
    setDescription(replacement ? (documentMetadataValue(replacement, 'description') ?? '') : '')
    setIssuer(replacement ? (documentMetadataValue(replacement, 'issuer') ?? '') : '')
    setReceivedAt('')
    setExpiresAt('')
    setError(null)
  }, [initialType, open, replacement])

  const selected = definitions.find((definition) => definition.type === type)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!file) {
      setError('Choose a PDF or image to upload.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      await onUpload({
        type,
        file,
        replacementId: replacement?.id ?? null,
        description: description.trim() || null,
        issuer: issuer.trim() || null,
        receivedAt: receivedAt || null,
        expiresAt: expiresAt || null,
      })
      onOpenChange(false)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to upload document')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{replacement ? 'Replace Document' : 'Upload Document'}</DialogTitle>
          <DialogDescription>
            {replacement
              ? `Upload a new version of ${replacement.file_name || documentDefinitionLabel(definitions, replacement.type)}.`
              : 'Attach a carrier, supplier, customer, or authority-issued document.'}
          </DialogDescription>
        </DialogHeader>
        <form className="min-w-0 space-y-5" onSubmit={submit}>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            <div className="min-w-0 space-y-2 sm:col-span-2">
              <Label htmlFor="uploaded-document-type">Document type</Label>
              <Select
                value={type}
                onValueChange={(value) => setType(value as ConsignmentDocumentType)}
                disabled={Boolean(replacement)}
              >
                <SelectTrigger id="uploaded-document-type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="start">
                  {options.map((definition) => (
                    <SelectItem key={definition.type} value={definition.type}>
                      {definition.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selected && <p className="text-sm text-muted-foreground">{selected.description}</p>}
            </div>
            <div className="min-w-0 space-y-2 sm:col-span-2">
              <Label htmlFor="document-file">File</Label>
              <Input
                id="document-file"
                type="file"
                accept="application/pdf,image/*"
                className="min-w-0"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
            </div>
            <div className="min-w-0 space-y-2">
              <Label htmlFor="document-issuer">Issuer</Label>
              <Input
                id="document-issuer"
                value={issuer}
                onChange={(event) => setIssuer(event.target.value)}
                placeholder="Supplier, carrier, or authority"
              />
            </div>
            <div className="min-w-0 space-y-2">
              <Label htmlFor="document-received-at">Received date</Label>
              <Input
                id="document-received-at"
                type="date"
                value={receivedAt}
                onChange={(event) => setReceivedAt(event.target.value)}
              />
            </div>
            <div className="min-w-0 space-y-2">
              <Label htmlFor="document-expires-at">Expiry date</Label>
              <Input
                id="document-expires-at"
                type="date"
                value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)}
              />
            </div>
            <div className="min-w-0 space-y-2 sm:col-span-2">
              <Label htmlFor="document-description">Description</Label>
              <Textarea
                id="document-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="What this file contains or why it matters"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {replacement ? 'Upload New Version' : 'Upload'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function DocumentPreviewDialog({
  document,
  definitions,
  onOpenChange,
}: {
  document: ConsignmentDocument | null
  definitions: ConsignmentDocumentDefinition[]
  onOpenChange: (open: boolean) => void
}) {
  const source = document ? documentHref(document) : ''

  return (
    <Dialog open={Boolean(document)} onOpenChange={onOpenChange}>
      <DialogContent className="h-[90vh] max-h-[90vh] overflow-hidden sm:max-w-5xl">
        {document && (
          <>
            <DialogHeader>
              <DialogTitle>
                {document.file_name || documentDefinitionLabel(definitions, document.type)}
              </DialogTitle>
              <DialogDescription>
                {documentDefinitionLabel(definitions, document.type)} · Version{' '}
                {document.version ?? 1}
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-hidden rounded-md border bg-muted/30">
              {isImageDocument(document) ? (
                <div className="flex h-full items-center justify-center overflow-auto p-4">
                  <img
                    src={source}
                    alt={document.file_name || documentDefinitionLabel(definitions, document.type)}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : (
                <iframe
                  src={source}
                  title={document.file_name || documentDefinitionLabel(definitions, document.type)}
                  className="h-full min-h-[65vh] w-full bg-background"
                />
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button asChild>
                <a href={source} download={document.file_name ?? undefined}>
                  Download
                </a>
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function SupplierContactDialog({
  open,
  onOpenChange,
  consignment,
  onChanged,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  consignment: ConsignmentDetail
  onChanged: () => void
}) {
  const details = consignment.supplier_details ?? {}
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName(String(details.name ?? details.supplier_name ?? ''))
    setEmail(String(details.email ?? details.supplier_email ?? ''))
    setPhone(String(details.phone ?? details.supplier_phone ?? ''))
    setError(null)
  }, [open, consignment.supplier_details])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!email.trim() && !phone.trim()) {
      setError('Add an email address or phone number for the supplier.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await apiClient.updateConsignment(consignment.id, {
        supplier_details: {
          ...details,
          name: name.trim() || null,
          email: email.trim() || null,
          phone: phone.trim() || null,
        },
      })
      onChanged()
      onOpenChange(false)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to save supplier contact')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Supplier Contact</DialogTitle>
          <DialogDescription>
            PVoC readiness requires a supplier email address or phone number.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="supplier-contact-name">Supplier</Label>
            <Input
              id="supplier-contact-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Supplier or factory name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="supplier-contact-email">Email</Label>
            <Input
              id="supplier-contact-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="supplier@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="supplier-contact-phone">Phone</Label>
            <Input
              id="supplier-contact-phone"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="+86 ..."
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Contact
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function DocumentsPanel({
  consignment,
  isAdmin,
  onChanged,
  onOpenCommunications,
}: {
  consignment: ConsignmentDetail
  isAdmin: boolean
  onChanged: () => void
  onOpenCommunications: () => void
}) {
  const definitions = consignment.document_capabilities?.length
    ? consignment.document_capabilities
    : fallbackDocumentDefinitions
  const [generateOpen, setGenerateOpen] = useState(false)
  const [generateType, setGenerateType] = useState<ConsignmentDocumentType | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadType, setUploadType] = useState<ConsignmentDocumentType | null>(null)
  const [replacement, setReplacement] = useState<ConsignmentDocument | null>(null)
  const [preview, setPreview] = useState<ConsignmentDocument | null>(null)
  const [supplierOpen, setSupplierOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const checklist = consignment.document_checklist ?? []

  function openGenerate(type?: ConsignmentDocumentType | null) {
    setGenerateType(type ?? null)
    setGenerateOpen(true)
  }

  function openUpload(type?: ConsignmentDocumentType | null, target?: ConsignmentDocument | null) {
    setUploadType(type ?? target?.type ?? null)
    setReplacement(target ?? null)
    setUploadOpen(true)
  }

  async function generateDocument(type: ConsignmentDocumentType) {
    const document = await apiClient.generateConsignmentDocument(consignment.id, type)
    setMessage(`Generated ${documentDefinitionLabel(definitions, document.type)}.`)
    setError(null)
    onChanged()
  }

  async function generateBookingNote(payload: GoxBookingNotePayload) {
    setBusy('gox-booking-note')
    setError(null)
    setMessage(null)
    try {
      const document = await apiClient.generateConsignmentDocument(
        consignment.id,
        'GOX_BOOKING_NOTE',
        {
          booking_note: payload,
        },
      )
      setMessage(`Generated ${formatLabel(document.type)}.`)
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to generate booking note')
    } finally {
      setBusy(null)
    }
  }

  async function uploadDocument(input: {
    type: ConsignmentDocumentType
    file: File
    replacementId: string | null
    description: string | null
    issuer: string | null
    receivedAt: string | null
    expiresAt: string | null
  }) {
    const upload = await apiClient.createStorageUpload({
      kind: 'invoice',
      file_name: input.file.name,
      content_type: input.file.type || 'application/octet-stream',
      size: input.file.size,
    })
    await apiClient.uploadToSignedUrl(upload, input.file)
    const payload = {
      type: input.type,
      file_url: upload.public_url,
      file_name: input.file.name,
      file_size_bytes: input.file.size,
      generated_by: 'CUSTOMER_UPLOAD',
      description: input.description,
      issuer: input.issuer,
      received_at: input.receivedAt,
      expires_at: input.expiresAt,
      content_type: input.file.type || 'application/octet-stream',
    } as const

    if (input.replacementId) {
      await apiClient.replaceConsignmentDocument(input.replacementId, payload)
    } else {
      await apiClient.attachConsignmentDocument(consignment.id, payload)
    }

    setMessage(
      input.replacementId
        ? `Uploaded a new version of ${input.file.name}.`
        : `Uploaded ${input.file.name}.`,
    )
    setError(null)
    onChanged()
  }

  async function deleteDocument(id: string) {
    setBusy(id)
    setError(null)
    setMessage(null)
    try {
      await apiClient.deleteConsignmentDocument(id)
      setMessage('Document archived.')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete document')
    } finally {
      setBusy(null)
    }
  }

  function handleRequirementAction(
    item: RequiredDocumentChecklistItem,
    action: RequiredDocumentChecklistItem['actions'][number],
  ) {
    const document = item.document_id
      ? (consignment.documents.find((candidate) => candidate.id === item.document_id) ?? null)
      : null
    if (action === 'PREVIEW' && document) setPreview(document)
    if (action === 'REPLACE' && document) openUpload(document.type, document)
    if (action === 'GENERATE' && item.type) openGenerate(item.type)
    if (action === 'UPLOAD' && item.type) openUpload(item.type)
    if (action === 'ADD_SUPPLIER_CONTACT') setSupplierOpen(true)
    if (action === 'REQUEST_CUSTOMER' || action === 'REQUEST_SUPPLIER') onOpenCommunications()
  }

  function requirementActionLabel(action: RequiredDocumentChecklistItem['actions'][number]) {
    if (action === 'PREVIEW') return 'Preview'
    if (action === 'REPLACE') return 'Replace'
    if (action === 'GENERATE') return 'Generate'
    if (action === 'UPLOAD') return 'Upload'
    if (action === 'ADD_SUPPLIER_CONTACT') return 'Add contact'
    if (action === 'REQUEST_SUPPLIER') return 'Request from supplier'
    return 'Request from customer'
  }

  return (
    <Card>
      <CardHeader className="gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1.5">
          <CardTitle>Document Center</CardTitle>
          <CardDescription>
            Required records, generated documents, and uploaded evidence for this consignment.
          </CardDescription>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <GoxBookingNoteDialog
              consignment={consignment}
              busy={busy === 'gox-booking-note'}
              onGenerate={generateBookingNote}
            />
            <Button type="button" variant="outline" onClick={() => openGenerate()}>
              <FilePlus2 className="h-4 w-4" />
              Generate
            </Button>
            <Button type="button" onClick={() => openUpload()}>
              <Upload className="h-4 w-4" />
              Upload
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {message && (
          <Alert>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        <section className="space-y-3">
          <div>
            <div className="font-medium">Requirements</div>
            <p className="text-sm text-muted-foreground">
              Based on the selected service and shipping method.
            </p>
          </div>
          {checklist.length === 0 ? (
            <p className="rounded-md border p-4 text-sm text-muted-foreground">
              No document requirements apply to this consignment.
            </p>
          ) : (
            <div className="divide-y rounded-md border">
              {checklist.map((item) => (
                <div
                  key={item.key}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    {item.present ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-done" />
                    ) : (
                      <XCircle className="h-4 w-4 shrink-0 text-destructive" />
                    )}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{item.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.present
                          ? item.document_version
                            ? `Version ${item.document_version} is ready`
                            : 'Contact information is ready'
                          : 'Required before this service is ready'}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={item.present ? 'secondary' : 'destructive'}>
                      {item.present ? 'Ready' : 'Missing'}
                    </Badge>
                    {item.actions.map((action) => (
                      <Button
                        key={action}
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!isAdmin && action !== 'PREVIEW'}
                        onClick={() => handleRequirementAction(item, action)}
                      >
                        {requirementActionLabel(action)}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <Separator />

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-medium">Files</div>
              <p className="text-sm text-muted-foreground">
                {consignment.documents.length} document
                {consignment.documents.length === 1 ? '' : 's'} across all versions.
              </p>
            </div>
          </div>
          {consignment.documents.length === 0 ? (
            <p className="rounded-md border p-4 text-sm text-muted-foreground">
              No documents have been generated or uploaded yet.
            </p>
          ) : (
            <div className="divide-y rounded-md border">
              {consignment.documents.map((document) => (
                <div
                  key={document.id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="truncate text-sm font-medium">
                        {document.file_name || formatLabel(document.type)}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                      <span>{documentDefinitionLabel(definitions, document.type)}</span>
                      <span>{formatLabel(document.generated_by)}</span>
                      <span>v{document.version ?? 1}</span>
                      <span>{formatLabel(document.status ?? 'ACTIVE')}</span>
                      {document.file_size_bytes && (
                        <span>{Math.max(1, Math.round(document.file_size_bytes / 1024))} KB</span>
                      )}
                      <span>{formatDate(document.created_at)}</span>
                    </div>
                    {documentMetadataValue(document, 'description') && (
                      <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">
                        {documentMetadataValue(document, 'description')}
                      </p>
                    )}
                    {documentMetadataValue(document, 'issuer') && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Issued by {documentMetadataValue(document, 'issuer')}
                      </p>
                    )}
                    {document.replaced_by_document_id && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <History className="h-3.5 w-3.5" />
                        Replaced by {document.replaced_by_document_id.slice(0, 8)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={() => setPreview(document)}
                    >
                      <Eye className="h-4 w-4" />
                      Preview
                    </Button>
                    {isAdmin && (
                      <>
                        {(document.status ?? 'ACTIVE') === 'ACTIVE' && (
                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            onClick={() => openUpload(document.type, document)}
                          >
                            Replace
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          type="button"
                          title="Archive document"
                          aria-label={`Archive ${document.file_name || documentDefinitionLabel(definitions, document.type)}`}
                          onClick={() => deleteDocument(document.id)}
                          disabled={busy === document.id}
                        >
                          {busy === document.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </CardContent>
      <GenerateDocumentDialog
        open={generateOpen}
        onOpenChange={setGenerateOpen}
        definitions={definitions}
        initialType={generateType}
        onGenerate={generateDocument}
      />
      <UploadDocumentDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        definitions={definitions}
        initialType={uploadType}
        replacement={replacement}
        onUpload={uploadDocument}
      />
      <DocumentPreviewDialog
        document={preview}
        definitions={definitions}
        onOpenChange={(open) => {
          if (!open) setPreview(null)
        }}
      />
      <SupplierContactDialog
        open={supplierOpen}
        onOpenChange={setSupplierOpen}
        consignment={consignment}
        onChanged={onChanged}
      />
    </Card>
  )
}

type SeaShipmentFormState = {
  reference: string
  trackingType: SeaTrackingType
  referenceKind: MaritimeReferenceKind
  carrierScac: string
  bookingNumber: string
  masterBillOfLadingNumber: string
  houseBillOfLadingNumber: string
  vesselName: string
  voyageNumber: string
  portOfLoading: string
  portOfDischarge: string
  estimatedDepartureAt: string
  estimatedArrivalAt: string
  status: MaritimeShipmentStatus
  notes: string
}

type SeaContainerFormState = {
  containerNumber: string
  equipmentCode: MaritimeEquipmentCode
  sealNumber: string
  status: MaritimeContainerStatus
  notes: string
}

const emptySeaShipmentForm = (): SeaShipmentFormState => ({
  reference: '',
  trackingType: 'booking',
  referenceKind: 'BOOKING',
  carrierScac: '',
  bookingNumber: '',
  masterBillOfLadingNumber: '',
  houseBillOfLadingNumber: '',
  vesselName: '',
  voyageNumber: '',
  portOfLoading: '',
  portOfDischarge: '',
  estimatedDepartureAt: '',
  estimatedArrivalAt: '',
  status: 'DRAFT',
  notes: '',
})

const emptySeaContainerForm = (): SeaContainerFormState => ({
  containerNumber: '',
  equipmentCode: '20GP',
  sealNumber: '',
  status: 'PLANNED',
  notes: '',
})

function seaShipmentFormFromRecord(shipment: ConsignmentSeaShipment): SeaShipmentFormState {
  const referenceKind = shipment.reference_kind || 'BOOKING'
  return {
    reference: shipment.reference,
    trackingType:
      shipment.tracking_type === 'container' && !isContainerReference(shipment.reference)
        ? trackingTypeForReferenceKind(referenceKind)
        : shipment.tracking_type,
    referenceKind,
    carrierScac: shipment.carrier_scac || '',
    bookingNumber: shipment.booking_number || '',
    masterBillOfLadingNumber: shipment.master_bill_of_lading_number || '',
    houseBillOfLadingNumber: shipment.house_bill_of_lading_number || '',
    vesselName: shipment.vessel_name || '',
    voyageNumber: shipment.voyage_number || '',
    portOfLoading: shipment.port_of_loading || '',
    portOfDischarge: shipment.port_of_discharge || '',
    estimatedDepartureAt: shipment.estimated_departure_at?.slice(0, 10) || '',
    estimatedArrivalAt: shipment.estimated_arrival_at?.slice(0, 10) || '',
    status: shipment.status || 'DRAFT',
    notes: shipment.notes || '',
  }
}

function seaContainerFormFromRecord(container: ConsignmentSeaContainer): SeaContainerFormState {
  return {
    containerNumber: container.container_number,
    equipmentCode:
      container.equipment_code ||
      (`${container.container_size || 20}${container.container_type || 'GP'}` as MaritimeEquipmentCode),
    sealNumber: container.seal_number || '',
    status: container.status || 'PLANNED',
    notes: container.notes || '',
  }
}

function seaContainerPayload(form: SeaContainerFormState) {
  return {
    container_number: form.containerNumber.trim(),
    equipment_code: form.equipmentCode,
    seal_number: form.sealNumber.trim() || null,
    status: form.status,
    notes: form.notes.trim() || null,
  }
}

function seaShipmentPayload(form: SeaShipmentFormState) {
  return {
    reference: form.reference.trim(),
    tracking_type: form.trackingType,
    reference_kind: form.referenceKind,
    carrier_scac: form.carrierScac.trim() || null,
    booking_number: form.bookingNumber.trim() || null,
    master_bill_of_lading_number: form.masterBillOfLadingNumber.trim() || null,
    house_bill_of_lading_number: form.houseBillOfLadingNumber.trim() || null,
    vessel_name: form.vesselName.trim() || null,
    voyage_number: form.voyageNumber.trim() || null,
    port_of_loading: form.portOfLoading.trim() || null,
    port_of_discharge: form.portOfDischarge.trim() || null,
    estimated_departure_at: form.estimatedDepartureAt
      ? new Date(`${form.estimatedDepartureAt}T00:00:00.000Z`).toISOString()
      : null,
    estimated_arrival_at: form.estimatedArrivalAt
      ? new Date(`${form.estimatedArrivalAt}T00:00:00.000Z`).toISOString()
      : null,
    status: form.status,
    notes: form.notes.trim() || null,
  }
}

async function fileToBase64(file: File) {
  const buffer = await file.arrayBuffer()
  let binary = ''
  const bytes = new Uint8Array(buffer)
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000))
  }
  return window.btoa(binary)
}

function SeaShipmentFields({
  form,
  setForm,
  referenceId,
  catalogs,
}: {
  form: SeaShipmentFormState
  setForm: (form: SeaShipmentFormState) => void
  referenceId: string
  catalogs: MaritimeCatalogs | null
}) {
  return (
    <div className="grid gap-3 md:grid-cols-4">
      <div className="space-y-2 md:col-span-2">
        <Label htmlFor={referenceId}>Shipment reference</Label>
        <Input
          id={referenceId}
          value={form.reference}
          onChange={(event) => setForm({ ...form, reference: event.target.value })}
          placeholder="Booking or bill of lading number"
        />
      </div>
      <div className="space-y-2">
        <Label>Reference kind</Label>
        <Select
          value={form.referenceKind}
          onValueChange={(value) =>
            setForm({
              ...form,
              referenceKind: value as MaritimeReferenceKind,
              trackingType: trackingTypeForReferenceKind(value as MaritimeReferenceKind),
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {maritimeReferenceKindOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {formatLabel(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Status</Label>
        <Select
          value={form.status}
          onValueChange={(value) => setForm({ ...form, status: value as MaritimeShipmentStatus })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {maritimeShipmentStatusOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {formatLabel(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Tracking method</Label>
        <Select
          value={form.trackingType}
          onValueChange={(value) => {
            const trackingType = value as SeaTrackingType
            const referenceKind = referenceKindForTrackingType(trackingType)
            setForm({
              ...form,
              trackingType,
              referenceKind: referenceKind ?? form.referenceKind,
            })
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {seaTrackingTypeOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {formatLabel(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Carrier</Label>
        <Select
          value={form.carrierScac}
          onValueChange={(value) => setForm({ ...form, carrierScac: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select verified carrier" />
          </SelectTrigger>
          <SelectContent>
            {(catalogs?.carriers ?? []).map((carrier) => (
              <SelectItem key={carrier.id} value={carrier.scac}>
                {carrier.name} ({carrier.scac})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Vessel</Label>
        <Input
          value={form.vesselName}
          onChange={(event) => setForm({ ...form, vesselName: event.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Voyage</Label>
        <Input
          value={form.voyageNumber}
          onChange={(event) => setForm({ ...form, voyageNumber: event.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Booking number</Label>
        <Input
          value={form.bookingNumber}
          onChange={(event) => setForm({ ...form, bookingNumber: event.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Master B/L</Label>
        <Input
          value={form.masterBillOfLadingNumber}
          onChange={(event) => setForm({ ...form, masterBillOfLadingNumber: event.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>House B/L</Label>
        <Input
          value={form.houseBillOfLadingNumber}
          onChange={(event) => setForm({ ...form, houseBillOfLadingNumber: event.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Port of loading</Label>
        <Input
          value={form.portOfLoading}
          onChange={(event) => setForm({ ...form, portOfLoading: event.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Port of discharge</Label>
        <Input
          value={form.portOfDischarge}
          onChange={(event) => setForm({ ...form, portOfDischarge: event.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Estimated departure</Label>
        <Input
          type="date"
          value={form.estimatedDepartureAt}
          onChange={(event) => setForm({ ...form, estimatedDepartureAt: event.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Estimated arrival</Label>
        <Input
          type="date"
          value={form.estimatedArrivalAt}
          onChange={(event) => setForm({ ...form, estimatedArrivalAt: event.target.value })}
        />
      </div>
      <div className="space-y-2 md:col-span-4">
        <Label>Notes</Label>
        <Textarea
          value={form.notes}
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
          rows={2}
        />
      </div>
    </div>
  )
}

function SeaContainerFields({
  form,
  setForm,
  containerId,
  equipmentTypes,
}: {
  form: SeaContainerFormState
  setForm: (form: SeaContainerFormState) => void
  containerId: string
  equipmentTypes: MaritimeCatalogs['equipment_types']
}) {
  return (
    <div className="grid gap-3 md:grid-cols-4">
      <div className="space-y-2">
        <Label htmlFor={containerId}>Container Number</Label>
        <Input
          id={containerId}
          value={form.containerNumber}
          onChange={(event) => setForm({ ...form, containerNumber: event.target.value })}
          placeholder="PCIU0107787"
        />
      </div>
      <div className="space-y-2">
        <Label>Equipment</Label>
        <Select
          value={form.equipmentCode}
          onValueChange={(value) =>
            setForm({ ...form, equipmentCode: value as MaritimeEquipmentCode })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(equipmentTypes.length
              ? equipmentTypes
              : maritimeEquipmentFallback.map((code) => ({
                  code,
                  label: code,
                  size_feet: Number(code.slice(0, 2)),
                  kind: code.slice(2),
                }))
            ).map((equipment) => (
              <SelectItem key={equipment.code} value={equipment.code}>
                {equipment.code} · {equipment.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Seal</Label>
        <Input
          value={form.sealNumber}
          onChange={(event) => setForm({ ...form, sealNumber: event.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Status</Label>
        <Select
          value={form.status}
          onValueChange={(value) => setForm({ ...form, status: value as MaritimeContainerStatus })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {maritimeContainerStatusOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {formatLabel(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2 md:col-span-4">
        <Label>Notes</Label>
        <Textarea
          value={form.notes}
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
          rows={2}
        />
      </div>
    </div>
  )
}

function SeaShipmentsPanel({
  consignment,
  isAdmin,
  onChanged,
}: {
  consignment: ConsignmentDetail
  isAdmin: boolean
  onChanged: () => void
}) {
  const [shipmentForm, setShipmentForm] = useState(emptySeaShipmentForm)
  const [newContainerForms, setNewContainerForms] = useState<SeaContainerFormState[]>([
    emptySeaContainerForm(),
  ])
  const [addShipmentOpen, setAddShipmentOpen] = useState(false)
  const [editingShipment, setEditingShipment] = useState<ConsignmentSeaShipment | null>(null)
  const [editingShipmentForm, setEditingShipmentForm] = useState(emptySeaShipmentForm)
  const [addingContainerShipmentId, setAddingContainerShipmentId] = useState<string | null>(null)
  const [containerForm, setContainerForm] = useState(emptySeaContainerForm)
  const [editingContainer, setEditingContainer] = useState<{
    shipmentId: string
    container: ConsignmentSeaContainer
  } | null>(null)
  const [editingContainerForm, setEditingContainerForm] = useState(emptySeaContainerForm)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [catalogs, setCatalogs] = useState<MaritimeCatalogs | null>(null)
  const { confirm, dialog: confirmDialog } = useConfirm()
  const [reconciliations, setReconciliations] = useState<MaritimeContainerReconciliation[]>([])
  const [billOfLadingOpen, setBillOfLadingOpen] = useState(false)
  const [billOfLadingFile, setBillOfLadingFile] = useState<File | null>(null)
  const [reviewProposal, setReviewProposal] = useState<BillOfLadingReviewProposal | null>(null)
  const [acceptedProposalFields, setAcceptedProposalFields] = useState<
    ApplyBillOfLadingProposalRequest['accepted_fields']
  >([])
  const [proposalContainerDecisions, setProposalContainerDecisions] = useState<
    Record<string, 'ADD' | 'IGNORE'>
  >({})
  const [reconciliationShipmentIds, setReconciliationShipmentIds] = useState<
    Record<string, string>
  >({})
  const [reconciliationContainerIds, setReconciliationContainerIds] = useState<
    Record<string, string>
  >({})

  const shipments = consignment.sea_shipments ?? []
  const containerCount = shipments.reduce((sum, shipment) => sum + shipment.containers.length, 0)

  useEffect(() => {
    let active = true
    void Promise.all([
      apiClient.getMaritimeCatalogs(),
      apiClient.listMaritimeReconciliations(consignment.id),
    ])
      .then(([nextCatalogs, nextReconciliations]) => {
        if (!active) return
        setCatalogs(nextCatalogs)
        setReconciliations(nextReconciliations)
      })
      .catch((loadError) => {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load maritime data')
        }
      })
    return () => {
      active = false
    }
  }, [consignment.id])

  async function createShipment(event: FormEvent) {
    event.preventDefault()
    setBusy('create-shipment')
    setError(null)
    setMessage(null)
    try {
      await apiClient.createConsignmentSeaShipment(consignment.id, {
        ...seaShipmentPayload(shipmentForm),
        containers: newContainerForms
          .filter((container) => container.containerNumber.trim())
          .map(seaContainerPayload),
      })
      setShipmentForm(emptySeaShipmentForm())
      setNewContainerForms([emptySeaContainerForm()])
      setAddShipmentOpen(false)
      setMessage('Sea shipment added.')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to add sea shipment')
    } finally {
      setBusy(null)
    }
  }

  function openEditShipment(shipment: ConsignmentSeaShipment) {
    setEditingShipment(shipment)
    setEditingShipmentForm(seaShipmentFormFromRecord(shipment))
    setError(null)
  }

  async function updateShipment(event: FormEvent) {
    event.preventDefault()
    if (!editingShipment) return
    setBusy(`shipment-${editingShipment.id}`)
    setError(null)
    setMessage(null)
    try {
      await apiClient.updateConsignmentSeaShipment(
        consignment.id,
        editingShipment.id,
        seaShipmentPayload(editingShipmentForm),
      )
      setEditingShipment(null)
      setMessage('Sea shipment updated.')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update sea shipment')
    } finally {
      setBusy(null)
    }
  }

  async function deleteShipment(shipment: ConsignmentSeaShipment) {
    const confirmed = await confirm({
      title: `Delete sea shipment ${shipment.reference}?`,
      description: 'Its containers are deleted with it. This cannot be undone.',
      action: 'Delete shipment',
      destructive: true,
    })
    if (!confirmed) return
    setBusy(`shipment-${shipment.id}`)
    setError(null)
    setMessage(null)
    try {
      await apiClient.deleteConsignmentSeaShipment(consignment.id, shipment.id)
      setMessage('Sea shipment deleted.')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete sea shipment')
    } finally {
      setBusy(null)
    }
  }

  async function addContainer(event: FormEvent) {
    event.preventDefault()
    if (!addingContainerShipmentId) return
    setBusy(`container-create-${addingContainerShipmentId}`)
    setError(null)
    setMessage(null)
    try {
      await apiClient.createConsignmentSeaContainer(
        consignment.id,
        addingContainerShipmentId,
        seaContainerPayload(containerForm),
      )
      setAddingContainerShipmentId(null)
      setContainerForm(emptySeaContainerForm())
      setMessage('Container added.')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to add container')
    } finally {
      setBusy(null)
    }
  }

  function openEditContainer(shipmentId: string, container: ConsignmentSeaContainer) {
    setEditingContainer({ shipmentId, container })
    setEditingContainerForm(seaContainerFormFromRecord(container))
    setError(null)
  }

  async function updateContainer(event: FormEvent) {
    event.preventDefault()
    if (!editingContainer) return
    setBusy(`container-${editingContainer.container.id}`)
    setError(null)
    setMessage(null)
    try {
      await apiClient.updateConsignmentSeaContainer(
        consignment.id,
        editingContainer.shipmentId,
        editingContainer.container.id,
        seaContainerPayload(editingContainerForm),
      )
      setEditingContainer(null)
      setMessage('Container updated.')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update container')
    } finally {
      setBusy(null)
    }
  }

  async function deleteContainer(shipmentId: string, container: ConsignmentSeaContainer) {
    const confirmed = await confirm({
      title: `Delete container ${container.container_number}?`,
      description: 'This cannot be undone.',
      action: 'Delete container',
      destructive: true,
    })
    if (!confirmed) return
    setBusy(`container-${container.id}`)
    setError(null)
    setMessage(null)
    try {
      await apiClient.deleteConsignmentSeaContainer(consignment.id, shipmentId, container.id)
      setMessage('Container deleted.')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete container')
    } finally {
      setBusy(null)
    }
  }

  async function reviewBillOfLading(event: FormEvent) {
    event.preventDefault()
    if (!billOfLadingFile) return
    setBusy('review-bl')
    setError(null)
    try {
      const proposal = await apiClient.createBillOfLadingProposal(consignment.id, {
        file_name: billOfLadingFile.name,
        content_base64: await fileToBase64(billOfLadingFile),
      })
      const acceptedFields = Object.entries(proposal.proposed_data)
        .filter(([field, value]) => field !== 'containers' && value !== null && value !== '')
        .map(([field]) => field) as ApplyBillOfLadingProposalRequest['accepted_fields']
      setReviewProposal(proposal)
      setAcceptedProposalFields(acceptedFields)
      setProposalContainerDecisions(
        Object.fromEntries(
          proposal.proposed_data.containers.map((container) => [
            container.container_number,
            'IGNORE',
          ]),
        ),
      )
    } catch (reviewError) {
      setError(
        reviewError instanceof Error ? reviewError.message : 'Failed to review bill of lading',
      )
    } finally {
      setBusy(null)
    }
  }

  async function applyBillOfLadingReview() {
    if (!reviewProposal) return
    setBusy('apply-bl')
    setError(null)
    try {
      await apiClient.applyBillOfLadingProposal(consignment.id, reviewProposal.id, {
        shipment_id: reviewProposal.shipment_id,
        accepted_fields: acceptedProposalFields,
        container_decisions: reviewProposal.proposed_data.containers.map((container) => ({
          container_number: container.container_number,
          decision: proposalContainerDecisions[container.container_number] || 'IGNORE',
        })),
      })
      setBillOfLadingOpen(false)
      setBillOfLadingFile(null)
      setReviewProposal(null)
      setMessage('Reviewed bill of lading fields applied.')
      onChanged()
    } catch (applyError) {
      setError(
        applyError instanceof Error ? applyError.message : 'Failed to apply bill of lading review',
      )
    } finally {
      setBusy(null)
    }
  }

  async function rejectBillOfLadingReview() {
    if (!reviewProposal) return
    setBusy('reject-bl')
    setError(null)
    try {
      await apiClient.rejectBillOfLadingProposal(consignment.id, reviewProposal.id)
      setBillOfLadingOpen(false)
      setBillOfLadingFile(null)
      setReviewProposal(null)
      setMessage('Bill of lading proposal rejected without changing operational records.')
    } catch (rejectError) {
      setError(
        rejectError instanceof Error
          ? rejectError.message
          : 'Failed to reject bill of lading review',
      )
    } finally {
      setBusy(null)
    }
  }

  async function resolveReconciliation(
    reconciliation: MaritimeContainerReconciliation,
    decision: 'ADD_AND_MAP' | 'MAP_EXISTING' | 'NOT_PART_OF_SHIPMENT' | 'REVIEW_LATER',
    shipmentId?: string,
    operationalContainerId?: string,
  ) {
    setBusy(`reconciliation-${reconciliation.id}`)
    setError(null)
    try {
      const updated = await apiClient.resolveMaritimeReconciliation(
        consignment.id,
        reconciliation.id,
        {
          decision,
          shipment_id: shipmentId || null,
          operational_container_id: operationalContainerId || null,
        },
      )
      setReconciliations((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      )
      if (decision !== 'REVIEW_LATER') onChanged()
    } catch (resolveError) {
      setError(
        resolveError instanceof Error
          ? resolveError.message
          : 'Failed to resolve provider container',
      )
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card>
      {confirmDialog}
      <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle>Sea Shipments</CardTitle>
          <CardDescription>
            {shipments.length.toLocaleString()} shipment{shipments.length === 1 ? '' : 's'} ·{' '}
            {containerCount.toLocaleString()} container{containerCount === 1 ? '' : 's'}
          </CardDescription>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => setBillOfLadingOpen(true)}>
              <FileText className="h-4 w-4" />
              Review B/L
            </Button>
            <Button
              type="button"
              onClick={() => setAddShipmentOpen(true)}
              aria-label="Add sea shipment"
            >
              <Ship className="h-4 w-4" />
              Add Shipment
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {message && (
          <Alert>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        {reconciliations.some((item) => !item.decision || item.decision === 'REVIEW_LATER') && (
          <div className="space-y-3 rounded-xl border border-waiting/50 bg-card p-4">
            <div>
              <h4 className="font-medium">Provider containers need review</h4>
              <p className="text-sm text-muted-foreground">
                Tracking data never changes Helm containers until an operator chooses an action.
              </p>
            </div>
            {reconciliations
              .filter((item) => !item.decision || item.decision === 'REVIEW_LATER')
              .map((reconciliation) => (
                <div
                  key={reconciliation.id}
                  className="grid gap-3 rounded-md border bg-background p-3 lg:grid-cols-[1fr_220px_auto]"
                >
                  <div>
                    <p className="font-medium">{reconciliation.provider_container_number}</p>
                    <p className="text-sm text-muted-foreground">
                      Discovered by {formatLabel(reconciliation.provider)}
                    </p>
                  </div>
                  <Select
                    value={reconciliationShipmentIds[reconciliation.id]}
                    onValueChange={(value) =>
                      setReconciliationShipmentIds((current) => ({
                        ...current,
                        [reconciliation.id]: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select shipment" />
                    </SelectTrigger>
                    <SelectContent>
                      {shipments.map((shipment) => (
                        <SelectItem key={shipment.id} value={shipment.id}>
                          {shipment.reference}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={
                        !reconciliationShipmentIds[reconciliation.id] ||
                        busy === `reconciliation-${reconciliation.id}`
                      }
                      onClick={() =>
                        resolveReconciliation(
                          reconciliation,
                          'ADD_AND_MAP',
                          reconciliationShipmentIds[reconciliation.id],
                        )
                      }
                    >
                      Add to shipment
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => resolveReconciliation(reconciliation, 'NOT_PART_OF_SHIPMENT')}
                    >
                      Not part
                    </Button>
                  </div>
                  <div className="lg:col-start-2">
                    <Select
                      value={reconciliationContainerIds[reconciliation.id]}
                      onValueChange={(value) =>
                        setReconciliationContainerIds((current) => ({
                          ...current,
                          [reconciliation.id]: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Or select existing container" />
                      </SelectTrigger>
                      <SelectContent>
                        {shipments.flatMap((shipment) =>
                          shipment.containers.map((container) => (
                            <SelectItem key={container.id} value={container.id}>
                              {container.container_number}
                            </SelectItem>
                          )),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={!reconciliationContainerIds[reconciliation.id]}
                      onClick={() =>
                        resolveReconciliation(
                          reconciliation,
                          'MAP_EXISTING',
                          undefined,
                          reconciliationContainerIds[reconciliation.id],
                        )
                      }
                    >
                      Map existing
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => resolveReconciliation(reconciliation, 'REVIEW_LATER')}
                    >
                      Review later
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        )}

        {shipments.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">
            No sea shipments have been attached to this consignment.
          </p>
        ) : (
          <div className="divide-y rounded-md border">
            {shipments.map((shipment) => (
              <div key={shipment.id} className="space-y-4 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Ship className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{shipment.reference}</span>
                      <Badge variant="outline">{formatLabel(shipment.tracking_type)}</Badge>
                      {shipment.status && (
                        <Badge variant="secondary">{formatLabel(shipment.status)}</Badge>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm text-muted-foreground">
                      <span>Containers: {shipment.containers.length}</span>
                      {shipment.carrier_scac && <span>Carrier: {shipment.carrier_scac}</span>}
                      {[shipment.vessel_name, shipment.voyage_number].filter(Boolean).length >
                        0 && (
                        <span>
                          Vessel:{' '}
                          {[shipment.vessel_name, shipment.voyage_number].filter(Boolean).join(' ')}
                        </span>
                      )}
                    </div>
                    {shipment.notes && (
                      <Translatable
                        text={shipment.notes}
                        context="note"
                        className="mt-2 text-sm text-muted-foreground"
                        label="the shipment notes"
                      />
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label={`Add container to ${shipment.reference}`}
                        onClick={() => {
                          setAddingContainerShipmentId(shipment.id)
                          setContainerForm(emptySeaContainerForm())
                        }}
                      >
                        <Package className="h-4 w-4" />
                        Container
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openEditShipment(shipment)}
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteShipment(shipment)}
                        disabled={busy === `shipment-${shipment.id}`}
                      >
                        {busy === `shipment-${shipment.id}` ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        Delete
                      </Button>
                    </div>
                  )}
                </div>

                {shipment.containers.length === 0 ? (
                  <p className="rounded-md border p-3 text-sm text-muted-foreground">
                    No containers have been recorded for this shipment.
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-xl border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Container</TableHead>
                          <TableHead>Size/Type</TableHead>
                          <TableHead>Seal</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Notes</TableHead>
                          {isAdmin && <TableHead className="w-[150px]">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {shipment.containers.map((container) => (
                          <TableRow key={container.id}>
                            <TableCell className="font-medium">
                              {container.container_number}
                            </TableCell>
                            <TableCell>
                              {container.equipment_code ||
                                [container.container_size, container.container_type]
                                  .filter(Boolean)
                                  .join(' ') ||
                                'Not set'}
                            </TableCell>
                            <TableCell>{container.seal_number || 'Not set'}</TableCell>
                            <TableCell>{formatLabel(container.status)}</TableCell>
                            <TableCell className="max-w-[280px] truncate">
                              {container.notes || 'Not set'}
                            </TableCell>
                            {isAdmin && (
                              <TableCell>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="xs"
                                    aria-label={`Edit container ${container.container_number}`}
                                    onClick={() => openEditContainer(shipment.id, container)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="xs"
                                    aria-label={`Delete container ${container.container_number}`}
                                    onClick={() => deleteContainer(shipment.id, container)}
                                    disabled={busy === `container-${container.id}`}
                                  >
                                    {busy === `container-${container.id}` ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={addShipmentOpen} onOpenChange={setAddShipmentOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <form onSubmit={createShipment} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Add Sea Shipment</DialogTitle>
              <DialogDescription>
                Use one record for each booking, bill of lading, or container-led shipment.
              </DialogDescription>
            </DialogHeader>
            <SeaShipmentFields
              form={shipmentForm}
              setForm={setShipmentForm}
              referenceId="new-sea-shipment-reference"
              catalogs={catalogs}
            />
            <Separator />
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-sm font-medium">Containers</h4>
                  <p className="text-sm text-muted-foreground">
                    Add every container included in this booking or bill of lading.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setNewContainerForms((current) => [...current, emptySeaContainerForm()])
                  }
                >
                  <Plus className="h-4 w-4" />
                  Add row
                </Button>
              </div>
              {newContainerForms.map((container, index) => (
                <div key={index} className="space-y-3 rounded-md border p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Container {index + 1}</span>
                    {newContainerForms.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove container row ${index + 1}`}
                        onClick={() =>
                          setNewContainerForms((current) =>
                            current.filter((_, currentIndex) => currentIndex !== index),
                          )
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <SeaContainerFields
                    form={container}
                    setForm={(nextContainer) =>
                      setNewContainerForms((current) =>
                        current.map((currentContainer, currentIndex) =>
                          currentIndex === index ? nextContainer : currentContainer,
                        ),
                      )
                    }
                    containerId={`new-sea-container-number-${index}`}
                    equipmentTypes={catalogs?.equipment_types ?? []}
                  />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddShipmentOpen(false)}
                disabled={busy === 'create-shipment'}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy === 'create-shipment' || !shipmentForm.reference.trim()}
              >
                {busy === 'create-shipment' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Ship className="h-4 w-4" />
                )}
                Add Shipment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(addingContainerShipmentId)}
        onOpenChange={(open) => !open && setAddingContainerShipmentId(null)}
      >
        <DialogContent className="sm:max-w-3xl">
          <form onSubmit={addContainer} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Add Container</DialogTitle>
              <DialogDescription>
                Add one operational container to the selected shipment.
              </DialogDescription>
            </DialogHeader>
            <SeaContainerFields
              form={containerForm}
              setForm={setContainerForm}
              containerId="new-container-number"
              equipmentTypes={catalogs?.equipment_types ?? []}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddingContainerShipmentId(null)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !containerForm.containerNumber.trim() ||
                  busy === `container-create-${addingContainerShipmentId}`
                }
              >
                {busy === `container-create-${addingContainerShipmentId}` ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={billOfLadingOpen}
        onOpenChange={(open) => {
          setBillOfLadingOpen(open)
          if (!open) {
            setBillOfLadingFile(null)
            setReviewProposal(null)
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Review Bill of Lading</DialogTitle>
            <DialogDescription>
              Extracted values remain a proposal until you explicitly apply selected fields and
              containers.
            </DialogDescription>
          </DialogHeader>
          {!reviewProposal ? (
            <form onSubmit={reviewBillOfLading} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bill-of-lading-file">Bill of lading PDF</Label>
                <Input
                  id="bill-of-lading-file"
                  type="file"
                  accept="application/pdf"
                  onChange={(event) => setBillOfLadingFile(event.target.files?.[0] ?? null)}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setBillOfLadingOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!billOfLadingFile || busy === 'review-bl'}>
                  {busy === 'review-bl' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  Extract for review
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Proposed shipment fields</h4>
                <div className="divide-y rounded-md border">
                  {Object.entries(reviewProposal.proposed_data)
                    .filter(([field, value]) => field !== 'containers' && value !== null)
                    .map(([field, value]) => {
                      const acceptedField =
                        field as ApplyBillOfLadingProposalRequest['accepted_fields'][number]
                      const provenance = reviewProposal.field_provenance[field]
                      return (
                        <label key={field} className="flex cursor-pointer items-start gap-3 p-3">
                          <Checkbox
                            checked={acceptedProposalFields.includes(acceptedField)}
                            onCheckedChange={(checked) =>
                              setAcceptedProposalFields((current) =>
                                checked
                                  ? [...new Set([...current, acceptedField])]
                                  : current.filter((item) => item !== acceptedField),
                              )
                            }
                          />
                          <span className="min-w-0">
                            <span className="block text-sm font-medium">{formatLabel(field)}</span>
                            <span className="block break-words text-sm">{String(value)}</span>
                            {provenance && (
                              <span className="block text-xs text-muted-foreground">
                                {Math.round(provenance.confidence * 100)}% confidence ·{' '}
                                {provenance.excerpt}
                              </span>
                            )}
                          </span>
                        </label>
                      )
                    })}
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Proposed containers</h4>
                {reviewProposal.proposed_data.containers.length === 0 ? (
                  <p className="rounded-md border p-3 text-sm text-muted-foreground">
                    No container numbers were extracted.
                  </p>
                ) : (
                  <div className="divide-y rounded-md border">
                    {reviewProposal.proposed_data.containers.map((container) => (
                      <div
                        key={container.container_number}
                        className="grid gap-3 p-3 sm:grid-cols-[1fr_180px] sm:items-center"
                      >
                        <div>
                          <p className="font-medium">{container.container_number}</p>
                          <p className="text-sm text-muted-foreground">
                            {container.equipment_code || 'Equipment not identified'} ·{' '}
                            {formatLabel(container.status)}
                          </p>
                        </div>
                        <Select
                          value={proposalContainerDecisions[container.container_number] || 'IGNORE'}
                          onValueChange={(value) =>
                            setProposalContainerDecisions((current) => ({
                              ...current,
                              [container.container_number]: value as 'ADD' | 'IGNORE',
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="IGNORE">Do not add</SelectItem>
                            <SelectItem value="ADD">Add to Helm</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={rejectBillOfLadingReview}
                  disabled={busy === 'reject-bl'}
                >
                  Reject proposal
                </Button>
                <Button
                  type="button"
                  onClick={applyBillOfLadingReview}
                  disabled={busy === 'apply-bl'}
                >
                  {busy === 'apply-bl' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  Apply selected
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(editingShipment)}
        onOpenChange={(open) => !open && setEditingShipment(null)}
      >
        <DialogContent className="sm:max-w-3xl">
          <form onSubmit={updateShipment} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Edit Sea Shipment</DialogTitle>
              <DialogDescription>
                Update the operational shipment record used by Helm.
              </DialogDescription>
            </DialogHeader>
            <SeaShipmentFields
              form={editingShipmentForm}
              setForm={setEditingShipmentForm}
              referenceId="edit-sea-shipment-reference"
              catalogs={catalogs}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingShipment(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !editingShipmentForm.reference.trim() ||
                  busy === `shipment-${editingShipment?.id}`
                }
              >
                {busy === `shipment-${editingShipment?.id}` ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(editingContainer)}
        onOpenChange={(open) => !open && setEditingContainer(null)}
      >
        <DialogContent className="sm:max-w-3xl">
          <form onSubmit={updateContainer} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Edit Container</DialogTitle>
              <DialogDescription>
                Update the container number, seal, status, and operational notes.
              </DialogDescription>
            </DialogHeader>
            <SeaContainerFields
              form={editingContainerForm}
              setForm={setEditingContainerForm}
              containerId="edit-container-number"
              equipmentTypes={catalogs?.equipment_types ?? []}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingContainer(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  !editingContainerForm.containerNumber.trim() ||
                  busy === `container-${editingContainer?.container.id}`
                }
              >
                {busy === `container-${editingContainer?.container.id}` ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function SeaTrackingLookupCard({
  consignment,
  isAdmin,
}: {
  consignment: ConsignmentDetail
  isAdmin: boolean
}) {
  const trackingTarget = getConsignmentSeaTrackingTarget(consignment)
  const reference = trackingTarget?.reference ?? null
  const trackingType = trackingTarget?.trackingType ?? null
  const shouldLookup = shouldLoadSeaTracking(consignment)
  const [registering, setRegistering] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [registrationError, setRegistrationError] = useState<string | null>(null)
  const [registrationMessage, setRegistrationMessage] = useState<string | null>(null)
  const [vesselPosition, setVesselPosition] = useState<SeaVesselCurrentPosition | null>(null)
  const [vesselPositionError, setVesselPositionError] = useState<Error | null>(null)
  const [positionNow, setPositionNow] = useState(() => Date.now())

  const { data, error, loading, reload } = useAsyncData(async () => {
    if (!reference || !trackingType || !shouldLookup) return null
    const [shipment, containerPage] = await Promise.all([
      apiClient.getSeaTrackingShipment(reference),
      apiClient.listSeaTrackingContainers(reference, 1, 25),
    ])

    return {
      shipment,
      containers: containerPage.containers,
    }
  }, [consignment.id, reference, shouldLookup, trackingType])

  const providerIsProcessing = data?.shipment?.process_status?.toLowerCase() === 'processing'

  useEffect(() => {
    if (!providerIsProcessing) return

    const pollTimer = window.setTimeout(() => reload(), 15_000)
    return () => window.clearTimeout(pollTimer)
    // Reload is intentionally omitted because useAsyncData returns a new callback each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerIsProcessing, reference, data?.shipment?.last_synced_at])

  useEffect(() => {
    if (!reference || !trackingType || !shouldLookup) {
      setVesselPosition(null)
      setVesselPositionError(null)
      return
    }

    let cancelled = false
    let pollTimer: number | undefined

    const pollVesselPosition = async () => {
      setPositionNow(Date.now())
      try {
        const nextPosition = await apiClient.getSeaVesselCurrentPosition(reference, consignment.id)
        if (!cancelled) {
          setVesselPosition(nextPosition)
          setVesselPositionError(null)
        }
      } catch (positionError) {
        if (!cancelled) {
          setVesselPositionError(
            positionError instanceof Error
              ? positionError
              : new Error('Failed to load the vessel position'),
          )
        }
      } finally {
        if (!cancelled) {
          pollTimer = window.setTimeout(pollVesselPosition, 30_000)
        }
      }
    }

    setVesselPosition(null)
    setVesselPositionError(null)
    void pollVesselPosition()

    return () => {
      cancelled = true
      if (pollTimer !== undefined) window.clearTimeout(pollTimer)
    }
  }, [reference, shouldLookup, trackingType])

  const visiwisePosition = useMemo(
    () => getSeaCurrentPosition(data?.containers ?? []),
    [data?.containers],
  )
  const aisPosition = useMemo(
    () => getVesselCurrentPosition(vesselPosition, positionNow),
    [positionNow, vesselPosition],
  )
  const currentPosition = useMemo(
    () => chooseSeaCurrentPosition(aisPosition, visiwisePosition),
    [aisPosition, visiwisePosition],
  )

  if (!reference || !trackingType || !shouldLookup) return null

  const shipment = data?.shipment ?? null
  const containers = data?.containers ?? []
  const containerCount = getShipmentContainerCount(shipment, containers)
  const firstContainer = containers[0]
  const routeSteps = buildSeaRouteSteps(shipment, containers)
  const movements = collectSeaMovements(containers, routeSteps)
  const currentJourney = describeCurrentJourney(currentPosition, routeSteps)
  const positionIsCurrent = hasCurrentVesselPosition(currentPosition)
  const positionAge = formatPositionAge(currentPosition?.date, positionNow)
  const listenerStartedAt = vesselPosition?.listener_started_at ?? null
  const providerLastCheckedAt = vesselPosition?.provider_last_checked_at ?? null
  const providerNextRefreshAt = vesselPosition?.provider_next_refresh_at ?? null
  const positionSource =
    currentPosition?.source === 'AISSTREAM'
      ? 'AISStream position report'
      : currentPosition?.source === 'DATA_DOCKED'
        ? 'Latest Data Docked vessel report'
        : currentPosition
          ? 'Carrier tracking milestone'
          : null
  const carrierProfile = getCarrierProfile(shipment, firstContainer)
  const lookupNotRegistered = Boolean(error && /shipment not found/i.test(error.message))
  const visibleError = registrationError || error?.message || null
  const hasTrackingDetails = containers.length > 0 || routeSteps.length > 0 || movements.length > 0

  async function registerSeaTracking() {
    if (!reference || !trackingType) return
    setRegistering(true)
    setRegistrationError(null)
    setRegistrationMessage(null)
    try {
      const customerName =
        consignment.customer?.company_name || consignment.customer?.name || 'Helm customer'
      const importResult = await apiClient.importSeaTrackingShipment({
        consignment_id: consignment.id,
        reference,
        tracking_type: trackingType,
        tracking_number: reference,
        carrier_scac: trackingTarget?.carrierScac,
        name: `${customerName} / ${reference}`,
        tags: ['helm', 'sea-tracking', trackingType],
        destination_country_code: 'UG',
      })

      if (importResult.total_failed > 0) {
        const importFailure = importResult.data
          .flatMap((item) => item.errors)
          .map((issue) => issue.message)
          .find(Boolean)
        throw new Error(importFailure || 'The sea tracking provider rejected this reference')
      }

      setRegistrationMessage(
        'Tracking registration accepted. Route and container data will appear after provider processing.',
      )
      reload()
    } catch (error) {
      setRegistrationError(
        error instanceof Error ? error.message : 'Failed to register sea tracking',
      )
    } finally {
      setRegistering(false)
    }
  }

  async function refreshSeaTracking() {
    if (!reference) return
    setRefreshing(true)
    setRegistrationError(null)
    setRegistrationMessage(null)
    try {
      const [, , refreshedPositionResult] = await Promise.all([
        apiClient.getSeaTrackingShipment(reference, { forceRefresh: true }),
        apiClient.listSeaTrackingContainers(reference, 1, 25, { forceRefresh: true }),
        apiClient
          .getSeaVesselCurrentPosition(reference, consignment.id)
          .then((position) => ({ position, error: null }))
          .catch((positionError: unknown) => ({
            position: null,
            error:
              positionError instanceof Error
                ? positionError
                : new Error('Failed to load the vessel position'),
          })),
      ])
      if (refreshedPositionResult.position) {
        setVesselPosition(refreshedPositionResult.position)
      }
      setVesselPositionError(refreshedPositionResult.error)
      setPositionNow(Date.now())
      setRegistrationMessage('Sea tracking refreshed.')
      reload()
    } catch (error) {
      setRegistrationError(
        error instanceof Error ? error.message : 'Failed to refresh sea tracking',
      )
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Map className="h-5 w-5" />
            Sea Tracking Command Center
          </CardTitle>
          <CardDescription>
            {trackingType === 'bill_of_lading'
              ? 'Bill of lading'
              : trackingType === 'booking'
                ? 'Booking reference'
                : trackingType === 'container'
                  ? 'Container reference'
                  : 'Shipment reference'}{' '}
            {reference}
            {shipment?.cached ? ' · using the last successful sync' : ''}
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={refreshSeaTracking}
            disabled={loading || refreshing}
          >
            <RefreshCw className={loading || refreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {visibleError && (
          <Alert variant={lookupNotRegistered && !registrationError ? 'default' : 'destructive'}>
            <AlertDescription>
              {lookupNotRegistered && !registrationError
                ? `Tracking has not been started for this ${trackingType === 'bill_of_lading' ? 'bill of lading' : trackingType} yet.`
                : visibleError}
            </AlertDescription>
          </Alert>
        )}
        {registrationMessage && (
          <Alert>
            <AlertDescription>{registrationMessage}</AlertDescription>
          </Alert>
        )}
        {loading && (
          <div className="flex items-center gap-2 rounded-md border p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading sea tracking...
          </div>
        )}
        {error && (
          <div className="space-y-3">
            {isAdmin && (
              <Button
                type="button"
                variant={lookupNotRegistered ? 'default' : 'outline'}
                onClick={registerSeaTracking}
                disabled={registering}
              >
                {registering ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Ship className="h-4 w-4" />
                )}
                Start Sea Tracking
              </Button>
            )}
          </div>
        )}
        {!loading && !error && shipment && (
          <>
            <div className="grid gap-3 md:grid-cols-5">
              <div className="rounded-md border p-3">
                <Label className="text-xs uppercase text-muted-foreground">Carrier</Label>
                <div className="mt-2">
                  <CarrierBadge profile={carrierProfile} />
                </div>
              </div>
              <div className="rounded-md border p-3">
                <Label className="text-xs uppercase text-muted-foreground">Tracking Status</Label>
                <div className="mt-2">
                  <Badge variant="secondary">
                    {formatLabel(shipment.process_status || shipment.data_status)}
                  </Badge>
                </div>
              </div>
              <div className="rounded-md border p-3">
                <Label className="text-xs uppercase text-muted-foreground">Data Freshness</Label>
                <p className="mt-2 font-medium">{formatLabel(shipment.data_freshness_status)}</p>
              </div>
              <div className="rounded-md border p-3">
                <Label className="text-xs uppercase text-muted-foreground">Containers</Label>
                <p className="mt-2 font-medium">{containerCount.toLocaleString()}</p>
              </div>
              <div className="rounded-md border p-3">
                <Label className="text-xs uppercase text-muted-foreground">Last Sync</Label>
                <p className="mt-2 font-medium">
                  {formatDateTime(
                    shipment.last_synced_at ||
                      shipment.last_successful_tracking_at ||
                      shipment.tracking_updated_at,
                  )}
                </p>
              </div>
            </div>

            {shipment.process_failure_reason && (
              <Alert variant="destructive">
                <AlertDescription>{shipment.process_failure_reason}</AlertDescription>
              </Alert>
            )}

            {providerIsProcessing && (
              <Alert>
                <Loader2 className="h-4 w-4 animate-spin" />
                <AlertDescription>
                  Visiwise is processing this bill of lading. Helm will refresh automatically while
                  route and container data are prepared.
                </AlertDescription>
              </Alert>
            )}

            {hasTrackingDetails ? (
              <div className="grid gap-4 xl:grid-cols-[minmax(0,0.72fr)_minmax(420px,1.28fr)]">
                <div className="space-y-4">
                  <div className="rounded-md border p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <Label className="text-xs uppercase text-muted-foreground">Container</Label>
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          <div>
                            <p className="text-lg font-semibold">
                              {firstContainer?.container_number || reference}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {[
                                firstContainer?.container_size
                                  ? `${firstContainer.container_size}'`
                                  : null,
                                firstContainer?.container_type,
                              ]
                                .filter(Boolean)
                                .join(' ') || 'Container type pending'}
                            </p>
                            <div className="mt-2">
                              <CarrierLogoOrCode profile={carrierProfile} />
                            </div>
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline">
                        {formatLabel(firstContainer?.process_status || shipment.process_status)}
                      </Badge>
                    </div>
                  </div>

                  <div className="rounded-md border p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <Label className="text-xs uppercase text-muted-foreground">
                          Last Movement
                        </Label>
                        <p className="mt-1 text-base font-medium">
                          {describeContainerMovement(firstContainer)}
                        </p>
                      </div>
                      {firstContainer?.pod_terminal_name && (
                        <Badge variant="outline">{firstContainer.pod_terminal_name}</Badge>
                      )}
                    </div>
                  </div>

                  <div className="rounded-md border p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <Label className="text-xs uppercase text-muted-foreground">
                          {positionIsCurrent
                            ? 'Current Vessel Position'
                            : 'Last Confirmed Carrier Event'}
                        </Label>
                        <p className="mt-1 text-base font-medium">
                          {currentPosition
                            ? currentPosition.source === 'VISIWISE' && currentPosition.event
                              ? `${formatLabel(currentPosition.event)} at ${currentPosition.name}`
                              : currentPosition.name
                            : 'Position unavailable'}
                        </p>
                        {currentPosition && (
                          <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                            <div className="pb-1">
                              <CarrierBadge profile={carrierProfile} />
                            </div>
                            <p>
                              {positionSource}
                              {currentPosition.meta ? ` · ${currentPosition.meta}` : ''}
                              {currentPosition.date
                                ? ` · ${formatDateTime(currentPosition.date)}`
                                : ''}
                              {positionAge ? ` · ${positionAge}` : ''}
                            </p>
                            {currentPosition.source === 'DATA_DOCKED' && providerLastCheckedAt && (
                              <p>
                                Helm checked Data Docked {formatDateTime(providerLastCheckedAt)}
                                {providerNextRefreshAt
                                  ? ` · Next automatic check ${formatDateTime(providerNextRefreshAt)}`
                                  : ''}
                              </p>
                            )}
                            {currentPosition.source === 'DATA_DOCKED' &&
                              vesselPosition?.provider_last_error_code && (
                                <p>
                                  The latest automatic provider check failed. Helm will retry
                                  automatically.
                                </p>
                              )}
                            {currentJourney !== currentPosition.name && (
                              <p>Route estimate: {currentJourney}</p>
                            )}
                            {(currentPosition.vessel || currentPosition.voyage) && (
                              <p>
                                {[currentPosition.vessel, currentPosition.voyage]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            )}
                            {(currentPosition.speedOverGroundKnots != null ||
                              currentPosition.courseOverGroundDegrees != null) && (
                              <p>
                                {[
                                  currentPosition.speedOverGroundKnots != null
                                    ? `${currentPosition.speedOverGroundKnots.toFixed(1)} kn`
                                    : null,
                                  currentPosition.courseOverGroundDegrees != null
                                    ? `Course ${Math.round(currentPosition.courseOverGroundDegrees)}°`
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            )}
                          </div>
                        )}
                        {vesselPosition?.state === 'WAITING_FOR_AIS' && (
                          <p className="mt-2 text-xs text-muted-foreground">
                            {vesselPosition.listener_state === 'CONNECTED'
                              ? 'AIS feed connected. '
                              : vesselPosition.listener_state === 'STALE'
                                ? 'AIS feed heartbeat is stale. '
                                : vesselPosition.listener_state === 'ERROR'
                                  ? 'AIS feed reported an error. '
                                  : vesselPosition.listener_state === 'NOT_RUNNING'
                                    ? 'AIS feed is not running. '
                                    : 'AIS feed is reconnecting. '}
                            No streaming AIS position has been received
                            {vesselPosition.mmsi ? ` for MMSI ${vesselPosition.mmsi}` : ''}
                            {listenerStartedAt
                              ? ` since ${formatDateTime(listenerStartedAt)}`
                              : ' during the current listener session'}
                            .
                            {vesselPosition.listener_heartbeat_at
                              ? ` Last heartbeat ${formatDateTime(vesselPosition.listener_heartbeat_at)}.`
                              : ''}
                            {currentPosition?.source === 'VISIWISE'
                              ? ' The carrier milestone above is not a live vessel position. Helm will also check its cached vessel-position providers.'
                              : ' Helm will show the next AISStream or cached provider position automatically.'}
                          </p>
                        )}
                        {vesselPosition?.state === 'NO_VESSEL_ID' && (
                          <p className="mt-2 text-xs text-muted-foreground">
                            Add the vessel MMSI or IMO to this sea shipment to enable live AIS
                            location updates.
                          </p>
                        )}
                        {vesselPositionError && (
                          <p className="mt-2 text-xs text-muted-foreground">
                            Live AIS service is unavailable.
                            {currentPosition?.source === 'VISIWISE'
                              ? ' Showing the latest synchronized tracking milestone.'
                              : ' Helm will try again automatically.'}
                          </p>
                        )}
                      </div>
                      <Badge variant="outline">
                        {currentPosition
                          ? getPositionLabel(currentPosition)
                          : vesselPosition?.state === 'WAITING_FOR_AIS'
                            ? vesselPosition.listener_state === 'CONNECTED'
                              ? 'Awaiting first AIS report'
                              : 'Waiting for AIS'
                            : vesselPosition?.state === 'NO_VESSEL_ID'
                              ? 'MMSI required'
                              : vesselPositionError
                                ? 'AIS unavailable'
                                : 'Live position unavailable'}
                      </Badge>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-md border p-4">
                      <Label className="text-xs uppercase text-muted-foreground">Origin</Label>
                      <p className="mt-2 font-medium">
                        {routeSteps[0]?.name ||
                          readString(firstContainer?.pol, 'name') ||
                          'Pending'}
                      </p>
                      {routeSteps[0]?.meta && (
                        <p className="mt-1 text-xs text-muted-foreground">{routeSteps[0].meta}</p>
                      )}
                    </div>
                    <div className="rounded-md border p-4">
                      <Label className="text-xs uppercase text-muted-foreground">Destination</Label>
                      <p className="mt-2 font-medium">
                        {routeSteps[routeSteps.length - 1]?.name ||
                          firstContainer?.pod_terminal_name ||
                          'Pending'}
                      </p>
                      {routeSteps[routeSteps.length - 1]?.meta && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {routeSteps[routeSteps.length - 1].meta}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <SeaRouteMap
                  routeSteps={routeSteps}
                  containers={containers}
                  reference={reference}
                  currentPosition={currentPosition}
                />
              </div>
            ) : (
              <div className="rounded-md border border-dashed p-6">
                <p className="font-medium">Tracking details are not available yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Helm has registered {reference} with Visiwise. Container movements and the route
                  map will appear here as soon as the provider returns tracking data.
                </p>
              </div>
            )}

            {routeSteps.length > 0 && (
              <SeaRouteMilestones routeSteps={routeSteps} containers={containers} />
            )}

            <SeaMovementsTable movements={movements} containers={containers} />

            {containers.length > 0 ? (
              <div className="overflow-hidden rounded-xl border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Container</TableHead>
                      <TableHead>Carrier</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Movement</TableHead>
                      <TableHead>POD</TableHead>
                      <TableHead>Vessel</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {containers.map((container, index) => {
                      const rowCarrierProfile = getCarrierProfile(shipment, container)
                      return (
                        <TableRow key={container.container_number || `${reference}-${index}`}>
                          <TableCell className="font-medium">
                            {container.container_number || reference}
                          </TableCell>
                          <TableCell>
                            <CarrierLogoOrCode profile={rowCarrierProfile} />
                          </TableCell>
                          <TableCell>{formatLabel(container.process_status)}</TableCell>
                          <TableCell>{describeContainerMovement(container)}</TableCell>
                          <TableCell>
                            {container.pod_terminal_name ||
                              readString(container.pod, 'name') ||
                              'Not set'}
                          </TableCell>
                          <TableCell>
                            {[container.arriving_vessel_name, container.arriving_vessel_voyage_no]
                              .filter(Boolean)
                              .join(' ') || 'Not set'}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="rounded-md border p-4 text-sm text-muted-foreground">
                The sea tracking feed returned the shipment but no container rows yet.
              </p>
            )}

            <div className="rounded-md border p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tracking Health</Label>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {shipment.no_data_reason || 'Tracking data is available for operations review.'}
                  </p>
                </div>
                {shipment.cached && <Badge variant="outline">Cached</Badge>}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function TrackingPanel({
  consignment,
  isAdmin,
  onChanged,
}: {
  consignment: ConsignmentDetail
  isAdmin: boolean
  onChanged: () => void
}) {
  const [trackingNumber, setTrackingNumber] = useState(
    displayTracking(consignment) === 'Not assigned' ? '' : displayTracking(consignment),
  )
  const [eventType, setEventType] = useState('STATUS_UPDATE')
  const [location, setLocation] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  // Gathering in China as changed here since the consignment last loaded.
  const [gatheringChange, setGatheringChange] = useState<{
    base: ConsignmentDetail['domestic_gathering']
    gathering: ConsignmentGathering
  } | null>(null)
  const gathering =
    gatheringChange && gatheringChange.base === consignment.domestic_gathering
      ? gatheringChange.gathering
      : consignment.domestic_gathering
  // A consignment that never goes by sea has no sea leg to show.
  const hasSeaLeg =
    consignment.shipping_method === 'SEA' || (consignment.sea_shipments?.length ?? 0) > 0

  async function recordTrackingEvent(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    setMessage(null)
    try {
      await apiClient.recordConsignmentTrackingEvent(consignment.id, {
        tracking_number: trackingNumber.trim() || null,
        event_type: eventType.trim(),
        location: location.trim() || null,
        description: description.trim() || null,
        occurred_at: new Date().toISOString(),
      })
      setDescription('')
      setMessage('Tracking update recorded.')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to record tracking update')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      {gathering && (
        <GatheringInChinaCard
          consignmentId={consignment.id}
          gathering={gathering}
          isAdmin={isAdmin}
          onChanged={(next) =>
            setGatheringChange({ base: consignment.domestic_gathering, gathering: next })
          }
        />
      )}
      {hasSeaLeg && (
        <SeaShipmentsPanel consignment={consignment} isAdmin={isAdmin} onChanged={onChanged} />
      )}
      {hasSeaLeg && <SeaTrackingLookupCard consignment={consignment} isAdmin={isAdmin} />}
      {isAdmin && (
        <PublicTrackingShareManager
          consignmentId={consignment.id}
          documents={consignment.documents}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Tracking Timeline</CardTitle>
          <CardDescription>
            Tracking updates recorded by hand or synced for the consignment. Courier scans in China
            show with each package above.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {message && (
            <Alert>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          {isAdmin && (
            <form
              onSubmit={recordTrackingEvent}
              className="grid gap-3 rounded-md border p-4 lg:grid-cols-[1fr_1fr_1fr_auto]"
            >
              <div className="space-y-2">
                <Label htmlFor="tracking-reference">Tracking Reference</Label>
                <Input
                  id="tracking-reference"
                  value={trackingNumber}
                  onChange={(event) => setTrackingNumber(event.target.value)}
                  placeholder="Container, B/L, waybill, domestic tracking"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tracking-event">Event</Label>
                <Input
                  id="tracking-event"
                  value={eventType}
                  onChange={(event) => setEventType(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tracking-location">Location</Label>
                <Input
                  id="tracking-location"
                  value={location}
                  onChange={(event) => setLocation(event.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={submitting || !eventType.trim()}>
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Record
                </Button>
              </div>
              <div className="space-y-2 lg:col-span-4">
                <Label htmlFor="tracking-description">Description</Label>
                <Textarea
                  id="tracking-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={3}
                  placeholder="Container discharged, customs exam booked, parcel arrived at warehouse..."
                />
              </div>
            </form>
          )}

          {consignment.tracking_events.length === 0 ? (
            <p className="rounded-md border p-4 text-sm text-muted-foreground">
              No tracking events have been synced for this consignment.
            </p>
          ) : (
            <div className="space-y-4">
              {consignment.tracking_events.map((event) => (
                <div key={event.id} className="grid gap-1 border-l-2 border-muted pl-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{formatLabel(event.source)}</Badge>
                    <span className="font-medium">{formatLabel(event.event_type)}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(event.occurred_at)} {event.location ? `at ${event.location}` : ''}
                  </div>
                  {event.description && <p className="text-sm">{event.description}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export function ConsignmentDetailPage() {
  const { id, tab } = useParams()
  const navigate = useNavigate()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const activeTab = isConsignmentTabValue(tab) ? tab : 'items'

  const { data, error, loading, reload, refresh } = useAsyncData(async () => {
    if (!id) throw new Error('Consignment id is missing')
    const [consignment, profile, communications] = await Promise.all([
      apiClient.getConsignment(id),
      apiClient.getProfile().catch(() => null),
      apiClient.listCommunications({ consignment_id: id, limit: 50 }).catch(() => []),
    ])
    return { consignment, profile, communications }
  }, [id])

  const consignment = data?.consignment
  const consignmentTasks = consignment?.tasks ?? []
  const communications = data?.communications ?? []
  const isAdmin = data?.profile?.is_admin || data?.profile?.role === 'admin'
  const customerName =
    consignment?.customer?.company_name || consignment?.customer?.name || 'Customer not linked'

  async function handleDelete() {
    if (!consignment) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await apiClient.deleteConsignment(consignment.id)
      navigate('/consignments')
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete consignment')
    } finally {
      setDeleting(false)
    }
  }

  if (loading) return <LoadingState label="Loading consignment..." />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!consignment) return <ErrorState error="Consignment not found" />

  // Named by its title, with its code beside it; never by a tracking number.
  const headerTitle = consignment.title || consignmentLabel(consignment)
  const headerCode = consignment.code || consignment.id.slice(0, 8)

  function handleTabChange(value: string) {
    if (!id || !isConsignmentTabValue(value)) return
    navigate(value === 'items' ? `/consignments/${id}` : `/consignments/${id}/${value}`)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={headerTitle}
        description={`${headerCode} · ${customerName} · ${formatLabel(consignment.status)}`}
        breadcrumbs={[{ label: 'Consignments', href: '/consignments' }, { label: headerCode }]}
        actions={
          <>
            <Button variant="outline" size="sm" asChild>
              <Link to="/consignments">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Link>
            </Button>
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditOpen(true)}
                  data-tour="consignment-edit"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              </>
            )}
          </>
        }
      />

      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={consignment.status} />
          {(consignment.service_types?.length
            ? consignment.service_types
            : [consignment.service_type]
          ).map((serviceType) => (
            <Badge key={serviceType} variant="secondary">
              {formatLabel(serviceType)}
            </Badge>
          ))}
          <Badge variant="outline">{formatLabel(consignment.workflow_phase)}</Badge>
          <StatusBadge status={consignment.operational_state} />
          <Badge variant="outline">
            {formatLabel(consignment.shipping_method || consignment.mode)}
          </Badge>
          <Badge variant="outline">{formatLabel(consignment.visibility)}</Badge>
          <Badge variant="secondary">{formatLabel(consignment.payment_status)}</Badge>
          {consignment.domestic_gathering &&
            (consignment.domestic_gathering.summary.items_with_number > 0 ||
              consignment.domestic_gathering.summary.packages_total > 0) && (
              // Informational: gathering in China never changes status or phase.
              <Link
                to={`/consignments/${consignment.id}/tracking`}
                className="rounded-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                <Badge
                  variant="outline"
                  className={
                    consignment.domestic_gathering.summary.all_at_warehouse
                      ? 'tone-done'
                      : 'tone-moving'
                  }
                >
                  <Warehouse aria-hidden />
                  {gatheringSummary(consignment.domestic_gathering.summary)}
                </Badge>
              </Link>
            )}
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          <FactCard label="Customer" value={customerName} icon={Package} />
          <FactCard
            label="Destination"
            value={consignment.destination_address || consignment.destination_port}
            icon={MapPin}
          />
          <FactCard
            label="Tracking"
            value={displayTracking(consignment)}
            icon={consignment.shipping_method === 'SEA' ? Ship : Truck}
          />
          <FactCard label="Created" value={formatDate(consignment.created_at)} icon={Calendar} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Workflow Context</CardTitle>
            <CardDescription>
              Operational phase, state, owner, and next accountable task.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Service</Label>
              <div className="mt-1 flex flex-wrap gap-1">
                {(consignment.service_types?.length
                  ? consignment.service_types
                  : [consignment.service_type]
                ).map((serviceType) => (
                  <Badge key={serviceType} variant="secondary">
                    {formatLabel(serviceType)}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Phase</Label>
              <p className="mt-1 font-medium">{formatLabel(consignment.workflow_phase)}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Owner</Label>
              <HelmOwnerName ownerUserId={consignment.owner_user_id} />
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Due</Label>
              <div className="mt-1 font-medium">
                <DueDateEditor
                  record={{
                    kind: 'consignment',
                    id: consignment.id,
                    customerId: consignment.customer_id,
                    customer: consignment.customer,
                    // The shipment's reference and title, never a package number.
                    reference: shipmentReference(consignment),
                  }}
                  dueAt={consignment.sla_due_at}
                  canEdit={Boolean(isAdmin)}
                  onChanged={refresh}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">State</Label>
              <p className="mt-1 font-medium">{formatLabel(consignment.operational_state)}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Preferred Channel</Label>
              <p className="mt-1 font-medium">
                {formatLabel(consignment.preferred_communication_channel)}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Last Communication</Label>
              <p className="mt-1 font-medium">
                {formatDateTime(consignment.last_communication_at)}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Next Task</Label>
              <p className="mt-1 font-medium">
                {consignmentTasks.find((task) => task.status === 'IN_PROGRESS')?.title ||
                  consignmentTasks.find((task) => task.status === 'TODO')?.title ||
                  consignment.next_action ||
                  'Not set'}
              </p>
            </div>
            {consignment.service_notes && (
              <div className="md:col-span-3">
                <Label className="text-xs uppercase text-muted-foreground">Notes</Label>
                <Translatable
                  text={consignment.service_notes}
                  context="note"
                  className="mt-1 whitespace-pre-wrap text-sm"
                  label="the service notes"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <RecordActivity
          createdAt={consignment.created_at}
          events={consignment.workflow_events ?? []}
          communications={communications}
        />

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList className="w-full" data-tour="consignment-tabs">
            <TabsTrigger value="items" onClick={() => handleTabChange('items')}>
              Items
            </TabsTrigger>
            <TabsTrigger value="tax" onClick={() => handleTabChange('tax')}>
              Tax Breakdown
            </TabsTrigger>
            <TabsTrigger value="documents" onClick={() => handleTabChange('documents')}>
              Documents
            </TabsTrigger>
            <TabsTrigger value="tracking" onClick={() => handleTabChange('tracking')}>
              Tracking
            </TabsTrigger>
            <TabsTrigger value="communications" onClick={() => handleTabChange('communications')}>
              Communications
            </TabsTrigger>
            <TabsTrigger value="customer" onClick={() => handleTabChange('customer')}>
              Customer
            </TabsTrigger>
          </TabsList>

          <TabsContent value="items">
            <EditableItemsPanel
              key={`${consignment.id}-items`}
              consignment={consignment}
              isAdmin={Boolean(isAdmin)}
              onChanged={refresh}
            />
          </TabsContent>

          <TabsContent value="tax">
            <TaxOperationsPanel
              key={`${consignment.id}-tax`}
              consignment={consignment}
              isAdmin={Boolean(isAdmin)}
              onChanged={reload}
            />
          </TabsContent>

          <TabsContent value="documents">
            <DocumentsPanel
              key={`${consignment.id}-documents`}
              consignment={consignment}
              isAdmin={Boolean(isAdmin)}
              onChanged={reload}
              onOpenCommunications={() => handleTabChange('communications')}
            />
          </TabsContent>

          <TabsContent value="tracking">
            <TrackingPanel
              key={`${consignment.id}-tracking`}
              consignment={consignment}
              isAdmin={Boolean(isAdmin)}
              onChanged={reload}
            />
          </TabsContent>

          <TabsContent value="communications">
            <CommunicationsPanel
              key={consignment.id}
              consignment={consignment}
              communications={communications}
              isAdmin={Boolean(isAdmin)}
              onChanged={reload}
            />
          </TabsContent>

          <TabsContent value="customer">
            <Card>
              <CardHeader>
                <CardTitle>{customerName}</CardTitle>
                <CardDescription>Customer contact and consignment ownership.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Email</Label>
                  <p className="mt-1 wrap-anywhere">{consignment.customer?.email || 'Not set'}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Phone</Label>
                  <p className="mt-1 wrap-anywhere">{consignment.customer?.phone || 'Not set'}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">TIN</Label>
                  <p className="mt-1 font-mono wrap-anywhere">
                    {consignment.customer?.tin_number || 'Not set'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">WhatsApp</Label>
                  <p className="mt-1 font-mono wrap-anywhere">
                    {consignment.customer?.whatsapp_group_id || 'Not set'}
                  </p>
                </div>
                {consignment.customer_id && (
                  <div className="md:col-span-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/customers/${consignment.customer_id}`}>Open Customer</Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <EditConsignmentDialog
        consignment={consignment}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={reload}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Consignment</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the consignment and its linked items, tax estimate,
              documents, and tracking records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
